import { TS, TC, T, SOLID, LIQUID, NONSOLID_PASS, BIOME_ATM } from './constants';
import { Perlin, SRng } from './WorldGen';

// === TILE TEXTURES ===
export class TileTextures {
    constructor(seed) { this.rng = new SRng(seed); }
    getKey(t, x, y) { return ((x * 374761393 + y * 668265263 + t * 1274126177) >>> 0) % 8; }
    shade(hex, amt) {
        if (!hex || hex[0] !== '#') return hex; let r = parseInt(hex.substr(1, 2), 16) + amt, g = parseInt(hex.substr(3, 2), 16) + amt, b = parseInt(hex.substr(5, 2), 16) + amt;
        return `rgb(${Math.max(0, Math.min(255, r))},${Math.max(0, Math.min(255, g))},${Math.max(0, Math.min(255, b))})`;
    }
    render(ctx, t, px, py, tx, ty, time) {
        const S = TS, k = this.getKey(t, tx, ty), c = TC[t]; if (!c) return;
        const v = ((tx * 2654435761 + ty * 2246822519) >>> 0) / 4294967296;
        const darker = this.shade(c, -15 - v * 10), lighter = this.shade(c, 15 + v * 10);
        switch (t) {
            case T.GRASS: case T.JGRASS: case T.CORGRASS: case T.HALGRASS: case T.MUSHGRASS:
            case T.SAVGRASS: case T.DFORESTGRASS: case T.FLOWERGRASS: case T.MEADOWGRASS:
            case T.CRYSTALGRASS: case T.BLOODGRASS: case T.SPOREGRASS: case T.PETRIGRASS: case T.PRISMGRASS:
            case T.STORMGRASS: case T.MUTATEDGRASS: {
                const dirts = {
                    [T.CORGRASS]: '#2a1030', [T.HALGRASS]: '#7799bb', [T.MUSHGRASS]: '#4a3a5a',
                    [T.SAVGRASS]: '#8a7020', [T.DFORESTGRASS]: '#3a2a10', [T.FLOWERGRASS]: '#6a5a14', [T.MEADOWGRASS]: '#6a5a14',
                    [T.CRYSTALGRASS]: '#6677bb', [T.BLOODGRASS]: '#4a1010', [T.SPOREGRASS]: '#2a1038',
                    [T.PETRIGRASS]: '#6a6050', [T.PRISMGRASS]: '#5aaa88',
                    [T.STORMGRASS]: '#3a3a4a', [T.MUTATEDGRASS]: '#3a2a4a'
                };
                const dc = dirts[t] || '#7a5a14'; ctx.fillStyle = dc; ctx.fillRect(px, py + 4, S, S - 4);
                ctx.fillStyle = this.shade(dc, v * 20 - 10); for (let i = 0; i < 3; i++)ctx.fillRect(px + (k * 3 + i * 5) % 13, py + 5 + (i * 7 + k) % 9, 3, 2);
                ctx.fillStyle = c; ctx.fillRect(px, py, S, 5); ctx.fillStyle = lighter; ctx.fillRect(px, py, S, 2);
                const gcs = {
                    [T.JGRASS]: '#2a9a1a', [T.CORGRASS]: '#7a3a8a', [T.HALGRASS]: '#aaccff',
                    [T.MUSHGRASS]: '#8a6aaa', [T.SAVGRASS]: '#c8b840', [T.DFORESTGRASS]: '#1a4a0a',
                    [T.FLOWERGRASS]: '#5aaa3a', [T.MEADOWGRASS]: '#7ac84a',
                    [T.CRYSTALGRASS]: '#aaccff', [T.BLOODGRASS]: '#bb4444', [T.SPOREGRASS]: '#aa44cc',
                    [T.PETRIGRASS]: '#aaa090', [T.PRISMGRASS]: '#bbddcc',
                    [T.STORMGRASS]: '#7a8a6a', [T.MUTATEDGRASS]: '#8855bb'
                };
                ctx.fillStyle = gcs[t] || '#5aaa3a';
                if (t === T.MUTATEDGRASS) {
                    // Animated glow for mutated grass
                    const mgPulse = Math.sin(time / 600 + tx * 0.5) * 0.15 + 0.85;
                    ctx.globalAlpha = mgPulse;
                }
                for (let i = 0; i < 4; i++)ctx.fillRect(px + (k + i * 4) % 14, py - 1 - ((i + k) % 2), 1, 2 + (i % 2));
                if (t === T.MUTATEDGRASS) ctx.globalAlpha = 1;
                break;
            }
            case T.DIRT: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S); ctx.fillStyle = darker;
                for (let i = 0; i < 4; i++)ctx.fillRect(px + (k * 5 + i * 7) % 12, py + (k * 3 + i * 11) % 12, 2, 2);
                ctx.fillStyle = lighter; ctx.fillRect(px + (k * 7) % 10, py + (k * 3) % 10, 3, 2); break;
            }
            case T.STONE: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S); ctx.fillStyle = darker;
                ctx.fillRect(px, py + (k % 3) * 5 + 2, S, 1); ctx.fillRect(px + (k % 4) * 4, py, 1, S);
                ctx.fillStyle = lighter; ctx.fillRect(px + (k * 3) % 11, py + (k * 7) % 11, 4, 3); break;
            }
            case T.SAND: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S); ctx.fillStyle = this.shade(c, v * 15);
                for (let i = 0; i < 5; i++)ctx.fillRect(px + (k + i * 3) % 14, py + (k * 7 + i * 5) % 14, 1, 1); break;
            }
            case T.SNOW: {
                ctx.fillStyle = '#e8e8f0'; ctx.fillRect(px, py + 3, S, S - 3); ctx.fillStyle = '#f0f0f5';
                ctx.fillRect(px, py, S, 4); ctx.fillStyle = '#fff'; ctx.fillRect(px + (k * 3) % 10, py + 1, 4, 1); break;
            }
            case T.WATER: {
                // Terraria-style variable water level rendering
                const wLvl = window._worldRef ? window._worldRef.gll(tx, ty) : 1.0;
                const fillH = Math.max(1, Math.round(S * Math.min(1.0, wLvl)));
                const topY = py + S - fillH;
                const wo = Math.sin(time / 400 + tx * 0.7 + ty * 0.3) * 1.2;
                // Check if tile above is also water — if so, fill completely
                const aboveWater = window._worldRef && LIQUID.has(window._worldRef.gt(tx, ty - 1));
                const drawTopY = aboveWater ? py : topY;
                const drawH = py + S - drawTopY;
                // Main water body
                ctx.fillStyle = 'rgba(20,70,190,0.55)';
                ctx.fillRect(px, drawTopY, S, drawH);
                // Surface highlight only if not fully covered from above
                if (!aboveWater && wLvl > 0.05) {
                    ctx.fillStyle = 'rgba(60,140,255,0.35)';
                    ctx.fillRect(px, drawTopY + wo, S, Math.min(3, drawH));
                    ctx.fillStyle = 'rgba(100,180,255,0.25)';
                    ctx.fillRect(px + 2, drawTopY + wo - 0.5, S - 4, 1);
                }
                // Depth shading
                if (drawH > 6) {
                    ctx.fillStyle = 'rgba(10,40,140,0.15)';
                    ctx.fillRect(px, drawTopY + drawH * 0.6, S, drawH * 0.4);
                }
                break;
            }
            case T.LAVA: {
                const lLvl = window._worldRef ? window._worldRef.gll(tx, ty) : 1.0;
                const lFillH = Math.max(1, Math.round(S * Math.min(1.0, lLvl)));
                const lTopY = py + S - lFillH;
                const aboveLava = window._worldRef && LIQUID.has(window._worldRef.gt(tx, ty - 1));
                const lDrawTopY = aboveLava ? py : lTopY;
                const lDrawH = py + S - lDrawTopY;
                const lo = Math.sin(time / 200 + tx * 0.8 + ty * 0.4), lo2 = Math.sin(time / 150 + tx * 1.2 + ty * 0.6);
                ctx.fillStyle = `rgb(${200 + lo * 40 | 0},${50 + lo * 30 | 0},0)`; ctx.fillRect(px, lDrawTopY, S, lDrawH);
                ctx.fillStyle = `rgba(255,${80 + lo * 60 | 0},0,0.7)`; ctx.fillRect(px + 2, lDrawTopY + 2, S - 4, Math.max(0, lDrawH - 4));
                ctx.fillStyle = `rgba(255,${180 + lo2 * 40 | 0},${50 + lo2 * 30 | 0},0.5)`; if (lDrawH > 6) ctx.fillRect(px + 4 + lo2 * 2, lDrawTopY + 4 + lo, 4, 3); break;
            }
            case T.TORCH: {
                ctx.fillStyle = '#6a4020'; ctx.fillRect(px + 6, py + 5, 4, 11); const fl = Math.sin(time / 80 + tx * 5) * 2;
                ctx.fillStyle = '#ff8800'; ctx.fillRect(px + 5, py + fl + 1, 6, 5); ctx.fillStyle = '#ffcc00'; ctx.fillRect(px + 6, py + fl + 2, 4, 3);
                ctx.fillStyle = '#fff'; ctx.fillRect(px + 7, py + fl + 2, 2, 1); break;
            }
            case T.DTORCH: {
                ctx.fillStyle = '#4a3050'; ctx.fillRect(px + 6, py + 5, 4, 11); const fl2 = Math.sin(time / 90 + tx * 5) * 2;
                ctx.fillStyle = '#cc44ff'; ctx.fillRect(px + 5, py + fl2 + 1, 6, 5); ctx.fillStyle = '#ee88ff'; ctx.fillRect(px + 6, py + fl2 + 2, 4, 3); break;
            }
            case T.LANTERN: {
                ctx.fillStyle = '#444'; ctx.fillRect(px + 5, py, 6, 3); const lg2 = Math.sin(time / 300 + tx) * 0.15 + 0.85;
                ctx.fillStyle = `rgba(255,204,68,${lg2})`; ctx.fillRect(px + 4, py + 3, 8, 8); ctx.fillStyle = '#444'; ctx.fillRect(px + 4, py + 11, 8, 2); break;
            }
            case T.LADDER: { ctx.fillStyle = '#c8a050'; ctx.fillRect(px + 2, py, 2, S); ctx.fillRect(px + S - 4, py, 2, S); ctx.fillRect(px + 2, py + 3, S - 4, 2); ctx.fillRect(px + 2, py + 10, S - 4, 2); break; }
            case T.CRYSTAL: case T.CRYSTSHARD: case T.ACRYSTAL: case T.RCRYSTAL: {
                ctx.fillStyle = c; ctx.fillRect(px + 2, py + 4, 5, 12); ctx.fillRect(px + 8, py + 2, 5, 14);
                const gw = Math.sin(time / 300 + tx + ty) * 0.3 + 0.7; ctx.fillStyle = `rgba(255,255,255,${gw * 0.4})`; ctx.fillRect(px + 3, py + 5, 3, 4); break;
            }
            case T.GLOWMUSH: {
                const gg = Math.sin(time / 500 + tx * 2 + ty) * 0.2 + 0.8; ctx.fillStyle = `rgba(100,255,160,${gg})`; ctx.fillRect(px + 6, py + 8, 4, 8);
                ctx.fillStyle = `rgba(68,255,136,${gg})`; ctx.fillRect(px + 2, py + 3, 12, 7); ctx.fillStyle = `rgba(140,255,200,${gg * 0.5})`; ctx.fillRect(px + 4, py + 4, 4, 3); break;
            }
            case T.CHEST: case T.DCHEST: case T.ANCCHEST: case T.FROZCHEST: {
                ctx.fillStyle = c; ctx.fillRect(px + 2, py + 4, 12, 10); ctx.fillStyle = this.shade(c, -20);
                ctx.fillRect(px + 2, py + 4, 12, 2); ctx.fillStyle = this.shade(c, 20); ctx.fillRect(px + 3, py + 6, 10, 1);
                ctx.fillStyle = '#ffd700'; ctx.fillRect(px + 6, py + 7, 4, 3); ctx.fillRect(px + 7, py + 6, 2, 1); break;
            }
            case T.VINE: case T.CAVEVINE: { ctx.fillStyle = c; ctx.fillRect(px + 5, py, 2, S); ctx.fillRect(px + 9, py + 3, 2, S - 3); ctx.fillStyle = this.shade(c, 20); ctx.fillRect(px + 4, py + (k * 3) % 10, 4, 3); break; }
            case T.FLOWER_R: case T.FLOWER_Y: case T.FLOWER_B: case T.FLOWER_P: case T.FLOWER_W: {
                ctx.fillStyle = '#2a7a1a'; ctx.fillRect(px + 7, py + 7, 2, 9);
                ctx.fillStyle = c; ctx.fillRect(px + 4, py + 2, 8, 6); ctx.fillStyle = t === T.FLOWER_W ? '#ff8800' : '#ffdd00'; ctx.fillRect(px + 7, py + 4, 2, 2); break;
            }
            case T.TALLGRASS: case T.DEADGRASS: case T.FERN: {
                const sway = Math.sin(time / 600 + tx * 1.3) * 1.5; ctx.fillStyle = c;
                ctx.fillRect(px + 3 + sway, py + 3, 2, 13); ctx.fillRect(px + 7 + sway * 0.7, py + 1, 2, 15); ctx.fillRect(px + 11 + sway * 0.5, py + 4, 2, 12);
                if (t === T.FERN) { ctx.fillRect(px + 5 + sway * 0.8, py + 5, 3, 1); ctx.fillRect(px + 9 + sway * 0.6, py + 7, 3, 1); } break;
            }
            case T.CACTUS: {
                ctx.fillStyle = '#1a6a10'; ctx.fillRect(px + 4, py, 8, S); ctx.fillStyle = '#2a8a1a'; ctx.fillRect(px + 5, py + 1, 6, S - 2);
                ctx.fillStyle = '#3aaa2a'; ctx.fillRect(px + 6, py + 2, 1, S - 4); ctx.fillStyle = '#0a5a08'; for (let i = 0; i < 3; i++)ctx.fillRect(px + 4 + (k + i * 4) % 7, py + (i * 5 + k * 3) % 12, 1, 1); break;
            }
            case T.MUSHROOM: {
                ctx.fillStyle = '#d0c8a0'; ctx.fillRect(px + 6, py + 8, 4, 8); ctx.fillStyle = '#d03030'; ctx.fillRect(px + 3, py + 3, 10, 7);
                ctx.fillStyle = '#fff'; ctx.fillRect(px + 5, py + 4, 2, 2); ctx.fillRect(px + 9, py + 5, 2, 2); break;
            }
            case T.COBWEB: {
                ctx.strokeStyle = 'rgba(220,220,220,0.5)'; ctx.lineWidth = 0.5; ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px + S, py + S);
                ctx.moveTo(px + S, py); ctx.lineTo(px, py + S); ctx.moveTo(px + S / 2, py); ctx.lineTo(px + S / 2, py + S); ctx.stroke();
                ctx.fillStyle = 'rgba(220,220,220,0.3)'; ctx.fillRect(px + 4, py + 4, 8, 8); break;
            }
            case T.STALAC: { ctx.fillStyle = '#707070'; ctx.fillRect(px + 4, py, 8, 4); ctx.fillRect(px + 5, py + 4, 6, 3); ctx.fillRect(px + 6, py + 7, 4, 4); ctx.fillRect(px + 7, py + 11, 2, 4); break; }
            case T.STALAGM: { ctx.fillStyle = '#686868'; ctx.fillRect(px + 7, py, 2, 4); ctx.fillRect(px + 6, py + 4, 4, 4); ctx.fillRect(px + 5, py + 8, 6, 4); ctx.fillRect(px + 4, py + 12, 8, 4); break; }
            case T.SPIKE: { ctx.fillStyle = '#555'; ctx.beginPath(); ctx.moveTo(px + 2, py + S); ctx.lineTo(px + S / 2, py + 2); ctx.lineTo(px + S - 2, py + S); ctx.fill(); break; }
            case T.BAMBOO: { ctx.fillStyle = '#4a8a1a'; ctx.fillRect(px + 5, py, 6, S); ctx.fillStyle = '#6aba2a'; ctx.fillRect(px + 6, py, 4, S); ctx.fillStyle = '#3a7a0a'; ctx.fillRect(px + 5, py + 7, 6, 2); break; }
            case T.LEAVES: case T.CHERRYLEAF: case T.WILLLEAF: case T.PALMLEAF: case T.PINELEAF: case T.ACACLEAF: case T.DARKLEAF: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S); ctx.fillStyle = lighter; ctx.fillRect(px + (k * 3) % 8, py + (k * 5) % 8, 6, 5); ctx.fillStyle = darker; ctx.fillRect(px + (k * 7) % 10, py + (k * 2) % 10, 4, 4); break;
            }
            case T.MUCAP: case T.GIANTMUSHCAP: { ctx.fillStyle = c; ctx.fillRect(px, py, S, S); ctx.fillStyle = '#fff'; ctx.fillRect(px + 3 + (k % 4), py + 3 + (k % 3), 3, 3); ctx.fillRect(px + 9 + (k % 3), py + 5 + (k % 4), 3, 3); break; }
            case T.SPAWNER: case T.DSPAWNER: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S); const sg = Math.sin(time / 150) * 0.5 + 0.5;
                ctx.fillStyle = `rgba(150,0,255,${sg * 0.5})`; ctx.fillRect(px + 2, py + 2, 12, 12); break;
            }
            case T.BIOVINE: {
                const bg = Math.sin(time / 400 + ty * 0.5) * 0.3 + 0.7; ctx.fillStyle = '#1a5a30'; ctx.fillRect(px + 6, py, 3, S);
                ctx.fillStyle = `rgba(34,204,136,${bg})`; ctx.fillRect(px + 5, py + 4, 5, 4); break;
            }
            case T.EMBER: { const eg = Math.sin(time / 150 + tx * 3) * 0.5 + 0.5; ctx.fillStyle = `rgb(${200 + eg * 55 | 0},${50 + eg * 40 | 0},0)`; ctx.fillRect(px + 3, py + 3, 10, 10); break; }
            case T.SAKURA: { const sw = Math.sin(time / 500 + tx) * 1; ctx.fillStyle = '#ffaacc'; ctx.fillRect(px + 4 + sw, py + 2, 3, 3); ctx.fillRect(px + 8 + sw * 0.7, py + 5, 3, 3); ctx.fillRect(px + 6 + sw * 0.5, py + 9, 3, 3); break; }
            case T.CATTAIL: { ctx.fillStyle = '#4a6a2a'; ctx.fillRect(px + 7, py + 4, 2, 12); ctx.fillStyle = '#6a4a20'; ctx.fillRect(px + 6, py + 1, 4, 5); break; }
            case T.SEASHELL: { ctx.fillStyle = '#f0e0d0'; ctx.fillRect(px + 4, py + 8, 8, 6); ctx.fillStyle = '#e8d0c0'; ctx.fillRect(px + 5, py + 9, 6, 4); ctx.fillStyle = '#ffccaa'; ctx.fillRect(px + 6, py + 10, 2, 2); break; }
            case T.COAL: { ctx.fillStyle = '#808080'; ctx.fillRect(px, py, S, S); ctx.fillStyle = '#303030'; ctx.fillRect(px + 2 + (k * 3) % 6, py + 2 + (k * 5) % 6, 5, 4); ctx.fillRect(px + 6 + (k * 2) % 5, py + 8 + (k * 7) % 5, 4, 4); break; }
            case T.IRON: {
                ctx.fillStyle = '#808080'; ctx.fillRect(px, py, S, S); ctx.fillStyle = '#c8a882'; ctx.fillRect(px + 3 + (k * 2) % 5, py + 3 + (k * 4) % 5, 4, 3); ctx.fillRect(px + 8 + (k * 3) % 4, py + 9 + (k * 5) % 4, 3, 3);
                ctx.fillStyle = '#d8b892'; ctx.fillRect(px + 4 + (k * 2) % 5, py + 4 + (k * 4) % 5, 2, 1); break;
            }
            case T.GOLD: {
                ctx.fillStyle = '#808080'; ctx.fillRect(px, py, S, S); ctx.fillStyle = '#ddb700'; ctx.fillRect(px + 2 + (k * 3) % 5, py + 2 + (k * 5) % 5, 4, 3); ctx.fillRect(px + 7 + (k * 2) % 4, py + 8 + (k * 3) % 4, 4, 4);
                ctx.fillStyle = '#ffe040'; ctx.fillRect(px + 3 + (k * 3) % 5, py + 3 + (k * 5) % 5, 2, 1); break;
            }
            case T.DIAMOND: {
                ctx.fillStyle = '#808080'; ctx.fillRect(px, py, S, S); ctx.fillStyle = '#00c8dd'; ctx.fillRect(px + 3 + (k % 4), py + 3 + (k % 3), 3, 4); ctx.fillRect(px + 9 + (k % 3), py + 8 + (k % 4), 3, 3);
                const dg = Math.sin(time / 400 + tx * 2 + ty) * 0.3 + 0.7; ctx.fillStyle = `rgba(100,255,255,${dg * 0.5})`; ctx.fillRect(px + 4 + (k % 4), py + 4 + (k % 3), 1, 2); break;
            }
            case T.COPPER: { ctx.fillStyle = '#808080'; ctx.fillRect(px, py, S, S); ctx.fillStyle = '#bb7733'; ctx.fillRect(px + 2 + (k * 3) % 5, py + 3 + (k * 4) % 5, 5, 3); ctx.fillRect(px + 8 + (k * 2) % 4, py + 9 + (k * 5) % 4, 3, 3); break; }
            case T.TIN: { ctx.fillStyle = '#808080'; ctx.fillRect(px, py, S, S); ctx.fillStyle = '#99a0a8'; ctx.fillRect(px + 3 + (k * 3) % 5, py + 2 + (k * 5) % 5, 4, 4); ctx.fillRect(px + 7 + (k * 2) % 4, py + 8 + (k * 3) % 4, 4, 3); break; }
            case T.AMETHYST: {
                ctx.fillStyle = '#808080'; ctx.fillRect(px, py, S, S); ctx.fillStyle = '#8833bb'; ctx.fillRect(px + 4 + (k % 4), py + 3 + (k % 3), 3, 5); ctx.fillRect(px + 9 + (k % 3), py + 7 + (k % 4), 3, 4);
                ctx.fillStyle = '#bb66ee'; ctx.fillRect(px + 5 + (k % 4), py + 4 + (k % 3), 1, 2); break;
            }
            case T.RUBY: { ctx.fillStyle = '#808080'; ctx.fillRect(px, py, S, S); ctx.fillStyle = '#bb1133'; ctx.fillRect(px + 3 + (k % 4), py + 4 + (k % 3), 4, 3); ctx.fillRect(px + 8 + (k % 3), py + 8 + (k % 4), 3, 3); break; }
            case T.EMERALD: { ctx.fillStyle = '#808080'; ctx.fillRect(px, py, S, S); ctx.fillStyle = '#11aa33'; ctx.fillRect(px + 3 + (k % 4), py + 3 + (k % 3), 3, 5); ctx.fillRect(px + 9 + (k % 3), py + 7 + (k % 4), 3, 4); break; }
            case T.SAPPHIRE: { ctx.fillStyle = '#808080'; ctx.fillRect(px, py, S, S); ctx.fillStyle = '#1133aa'; ctx.fillRect(px + 3 + (k % 4), py + 4 + (k % 3), 4, 4); ctx.fillRect(px + 8 + (k % 3), py + 8 + (k % 4), 3, 3); break; }
            case T.DBRICK: case T.ANCBRICK: case T.CRYPTBRICK: case T.MAGBRICK: case T.ICEBRICK: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S); ctx.fillStyle = darker;
                ctx.fillRect(px, py + 7, S, 1); ctx.fillRect(px + ((ty % 2) * 8 + 3) % S, py, 1, 7); ctx.fillStyle = lighter; ctx.fillRect(px + 2 + (k % 5), py + 2 + (k % 3), 3, 2); break;
            }
            case T.DPILLAR: case T.ANCPILLAR: { ctx.fillStyle = c; ctx.fillRect(px + 2, py, 12, S); ctx.fillStyle = lighter; ctx.fillRect(px + 3, py, 2, S); ctx.fillStyle = darker; ctx.fillRect(px + 12, py, 2, S); break; }
            case T.ROOT: {
                ctx.fillStyle = '#5a3a1a'; ctx.fillRect(px + 5, py, 3, S); ctx.fillRect(px + 8, py + 4, 3, S - 4);
                ctx.fillStyle = '#6a4a2a'; ctx.fillRect(px + 6, py + 1, 1, S - 2); break;
            }
            case T.BONEPILE: {
                ctx.fillStyle = '#d8d0b8'; ctx.fillRect(px + 2, py + 10, 12, 4); ctx.fillRect(px + 3, py + 8, 4, 3); ctx.fillRect(px + 8, py + 9, 4, 2);
                ctx.fillStyle = '#c8c0a8'; ctx.fillRect(px + 5, py + 7, 2, 4); break;
            }
            case T.MINEBEAM: {
                ctx.fillStyle = '#7a5a2a'; ctx.fillRect(px + 1, py, 4, S); ctx.fillRect(px + S - 5, py, 4, S); ctx.fillRect(px, py, S, 3);
                ctx.fillStyle = '#8a6a3a'; ctx.fillRect(px + 2, py + 1, 2, S - 2); break;
            }
            // === VILLAGE TILES ===
            case T.PLANK: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = darker; ctx.fillRect(px, py + 3, S, 1); ctx.fillRect(px, py + 10, S, 1);
                ctx.fillStyle = lighter; ctx.fillRect(px + (k * 3) % 8, py + 1, 4, 1); ctx.fillRect(px + (k * 5) % 10, py + 7, 3, 1); break;
            }
            case T.GLASS: {
                ctx.fillStyle = 'rgba(140,190,230,0.45)'; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = 'rgba(200,230,255,0.35)'; ctx.fillRect(px + 2, py + 2, S - 4, S - 4);
                ctx.fillStyle = 'rgba(255,255,255,0.25)'; ctx.fillRect(px + 3, py + 3, 4, 2);
                ctx.strokeStyle = 'rgba(100,140,180,0.5)'; ctx.lineWidth = 0.5;
                ctx.strokeRect(px + 0.5, py + 0.5, S - 1, S - 1); break;
            }
            case T.FENCE: {
                ctx.fillStyle = c; ctx.fillRect(px + 6, py, 3, S);
                ctx.fillStyle = darker; ctx.fillRect(px + 3, py + 3, 9, 2); ctx.fillRect(px + 3, py + 10, 9, 2);
                ctx.fillStyle = lighter; ctx.fillRect(px + 7, py, 1, S); break;
            }
            case T.BOOKSHELF: {
                ctx.fillStyle = '#5a3018'; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = '#7a4a28'; ctx.fillRect(px, py + 7, S, 1);
                const bcs = ['#cc3333', '#3366cc', '#33aa33', '#cc9933', '#993399', '#339999', '#cc6633', '#6633cc'];
                for (let i = 0; i < 4; i++) { ctx.fillStyle = bcs[(k + i) % bcs.length]; ctx.fillRect(px + 1 + i * 3 + (i > 1 ? 1 : 0), py + 1, 3, 5); }
                for (let i = 0; i < 4; i++) { ctx.fillStyle = bcs[(k + i + 3) % bcs.length]; ctx.fillRect(px + 1 + i * 3 + (i > 1 ? 1 : 0), py + 9, 3, 5); }
                break;
            }
            case T.HAY: {
                ctx.fillStyle = '#c4a838'; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = '#d4b848'; for (let i = 0; i < 6; i++) ctx.fillRect(px + (k + i * 3) % 13, py + (k * 5 + i * 4) % 13, 1, 3);
                ctx.fillStyle = '#b49828'; ctx.fillRect(px + (k * 2) % 10, py + (k * 3) % 10, 4, 2); break;
            }
            case T.BARREL: {
                ctx.fillStyle = '#6a4a20'; ctx.fillRect(px + 2, py, 12, S);
                ctx.fillStyle = '#5a3a18'; ctx.fillRect(px + 3, py + 3, 10, 2); ctx.fillRect(px + 3, py + 11, 10, 2);
                ctx.fillStyle = '#888'; ctx.fillRect(px + 2, py + 2, 12, 1); ctx.fillRect(px + 2, py + 13, 12, 1);
                ctx.fillStyle = lighter; ctx.fillRect(px + 4, py + 5, 1, 5); break;
            }
            case T.COBBLEPATH: {
                ctx.fillStyle = '#7a7a70'; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = '#8a8a80'; ctx.fillRect(px + 1 + (k % 3), py + 1 + (k % 2), 5, 4);
                ctx.fillRect(px + 8 + (k % 3), py + 7 + (k % 3), 5, 4);
                ctx.fillStyle = '#6a6a60'; ctx.fillRect(px + 7 + (k % 2), py + 2, 4, 3);
                ctx.fillRect(px + 2, py + 8 + (k % 3), 4, 4);
                ctx.fillStyle = '#9a9a90'; ctx.fillRect(px + 3 + (k * 3) % 6, py + (k * 5) % 8, 2, 1); break;
            }
            case T.CHIMNEY: {
                ctx.fillStyle = '#4a3a3a'; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = '#5a4a4a'; ctx.fillRect(px + 2, py + 2, S - 4, S - 4);
                ctx.fillStyle = '#3a2a2a'; ctx.fillRect(px, py + 7, S, 1); ctx.fillRect(px + ((ty % 2) * 7) % S, py, 1, 7); break;
            }
            case T.FLOWERPOT: {
                ctx.fillStyle = '#b05020'; ctx.fillRect(px + 4, py + 6, 8, 8);
                ctx.fillStyle = '#c06030'; ctx.fillRect(px + 3, py + 6, 10, 2);
                ctx.fillStyle = '#5a3a1a'; ctx.fillRect(px + 5, py + 5, 6, 2);
                const pc = ['#ff3344', '#ffdd00', '#ff88aa', '#8844ff'][(k + tx) % 4];
                ctx.fillStyle = pc; ctx.fillRect(px + 6, py + 1, 4, 4); ctx.fillRect(px + 5, py + 2, 2, 2); ctx.fillRect(px + 10, py + 2, 2, 2);
                ctx.fillStyle = '#2a6a1a'; ctx.fillRect(px + 7, py + 4, 2, 2); break;
            }
            case T.VDOOR: {
                ctx.fillStyle = '#5a3a18'; ctx.fillRect(px + 2, py, 12, S);
                ctx.fillStyle = '#6a4a28'; ctx.fillRect(px + 3, py + 1, 10, S - 2);
                ctx.fillStyle = '#4a2a10'; ctx.fillRect(px + 4, py + 2, 1, S - 4); ctx.fillRect(px + 11, py + 2, 1, S - 4);
                ctx.fillStyle = '#aa8844'; ctx.fillRect(px + 10, py + 7, 2, 2); break;
            }
            case T.VPILLAR: {
                ctx.fillStyle = c; ctx.fillRect(px + 3, py, 10, S);
                ctx.fillStyle = lighter; ctx.fillRect(px + 4, py, 2, S);
                ctx.fillStyle = darker; ctx.fillRect(px + 11, py, 2, S);
                ctx.fillStyle = lighter; ctx.fillRect(px + 2, py, 12, 2); ctx.fillRect(px + 2, py + S - 2, 12, 2); break;
            }
            case T.VILLSIGN: {
                ctx.fillStyle = '#6a4020'; ctx.fillRect(px + 7, py + 6, 2, 10);
                ctx.fillStyle = '#8a6a3a'; ctx.fillRect(px + 1, py + 2, 14, 6);
                ctx.fillStyle = '#9a7a4a'; ctx.fillRect(px + 2, py + 3, 12, 4);
                ctx.fillStyle = '#5a3a1a'; ctx.fillRect(px + 4, py + 4, 8, 2); break;
            }
            case T.THATCH: {
                ctx.fillStyle = '#b09030'; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = '#c0a040'; for (let i = 0; i < 4; i++) ctx.fillRect(px + (k + i * 4) % 12, py + i * 4, 4, 2);
                ctx.fillStyle = '#a08020'; ctx.fillRect(px + (k * 3) % 8, py + (k * 5) % 10, 3, 3); break;
            }
            case T.CLAYBRICK: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = this.shade(c, -20); ctx.fillRect(px, py + 7, S, 1); ctx.fillRect(px + ((ty % 2) * 8 + 4) % S, py, 1, 7);
                ctx.fillStyle = this.shade(c, 15); ctx.fillRect(px + 2, py + 2 + (k % 3), 3, 2); break;
            }
            // === SKY ISLAND TILES ===
            case T.SKYSTONE: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = this.shade(c, 15); ctx.fillRect(px + 2 + (k % 4), py + 2 + (k % 3), 4, 3);
                ctx.fillStyle = this.shade(c, -10); ctx.fillRect(px, py + 7, S, 1); ctx.fillRect(px + ((ty % 2) * 8 + 3) % S, py, 1, 7); break;
            }
            case T.CLOUDBLOCK: {
                ctx.fillStyle = '#dde8f0'; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = '#eef3f8'; ctx.fillRect(px + 1, py + 1, S - 2, S - 2);
                ctx.fillStyle = '#fff'; ctx.fillRect(px + 3 + (k % 4), py + 3 + (k % 3), 5, 4); break;
            }
            case T.STARBLOCK: {
                const starGlow = Math.sin(time / 400 + tx * 2.3 + ty * 1.7) * 0.3 + 0.7;
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = `rgba(200,220,255,${starGlow * 0.5})`; ctx.fillRect(px + 4, py + 4, 8, 8);
                ctx.fillStyle = `rgba(255,255,255,${starGlow})`; ctx.fillRect(px + 6, py + 6, 4, 4); break;
            }
            case T.SKYGRASS: {
                ctx.fillStyle = '#8899cc'; ctx.fillRect(px, py + 4, S, S - 4);
                ctx.fillStyle = c; ctx.fillRect(px, py, S, 5);
                ctx.fillStyle = '#bbddcc'; ctx.fillRect(px, py, S, 2);
                ctx.fillStyle = '#aaccbb';
                for (let i = 0; i < 3; i++) ctx.fillRect(px + (k + i * 5) % 13, py - 1, 1, 2 + (i % 2)); break;
            }
            case T.SUNPLATE: {
                const sg2 = Math.sin(time / 300 + tx + ty) * 0.2 + 0.8;
                ctx.fillStyle = `rgb(${220 + sg2 * 35 | 0},${190 + sg2 * 40 | 0},${60 + sg2 * 30 | 0})`; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = `rgba(255,255,200,${sg2 * 0.3})`; ctx.fillRect(px + 2, py + 2, S - 4, S - 4);
                ctx.fillStyle = this.shade(c, -20); ctx.fillRect(px, py + 7, S, 1); break;
            }
            // === NEW BIOME TILES ===
            case T.CRYSTALBLOCK: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S);
                const cbg = Math.sin(time / 500 + tx + ty * 0.5) * 0.15 + 0.85;
                ctx.fillStyle = `rgba(140,160,230,${cbg})`; ctx.fillRect(px + 2 + (k % 4), py + 2 + (k % 3), 5, 4);
                ctx.fillStyle = this.shade(c, -15); ctx.fillRect(px, py + 7, S, 1); break;
            }
            case T.ABYSSSTONE: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = '#0a0a18'; ctx.fillRect(px + (k * 3) % 10, py + (k * 5) % 10, 4, 3);
                ctx.fillStyle = '#2a2a3a'; ctx.fillRect(px + (k * 7) % 12, py + (k * 2) % 12, 2, 2); break;
            }
            case T.ABYSSMOSS: {
                ctx.fillStyle = '#0a1a28'; ctx.fillRect(px, py + 3, S, S - 3);
                ctx.fillStyle = c; ctx.fillRect(px, py, S, 4);
                const abg = Math.sin(time / 600 + tx * 1.5) * 0.2 + 0.6;
                ctx.fillStyle = `rgba(0,100,140,${abg})`; ctx.fillRect(px, py, S, 2); break;
            }
            case T.EMBERITE: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S);
                const eg2 = Math.sin(time / 200 + tx * 2 + ty) * 0.3 + 0.7;
                ctx.fillStyle = `rgba(255,100,20,${eg2 * 0.3})`; ctx.fillRect(px + 3, py + 3, S - 6, S - 6);
                ctx.fillStyle = this.shade(c, -20); ctx.fillRect(px, py + 8, S, 1); break;
            }
            case T.EMBERSAND: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S);
                const es = Math.sin(time / 300 + tx * 1.5) * 0.15 + 0.85;
                ctx.fillStyle = `rgba(200,120,50,${es})`;
                for (let i = 0; i < 5; i++) ctx.fillRect(px + (k + i * 3) % 14, py + (k * 7 + i * 5) % 14, 1, 1); break;
            }
            case T.RUINBRICK: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = darker; ctx.fillRect(px, py + 7, S, 1);
                ctx.fillRect(px + ((ty % 2) * 8 + 3) % S, py, 1, 7);
                ctx.fillStyle = '#3a5a2a'; ctx.fillRect(px + (k * 2) % 8, py + (k * 3) % 6, 3, 2); break;
            }
            case T.RUINMOSS: {
                ctx.fillStyle = '#4a5a3a'; ctx.fillRect(px, py + 3, S, S - 3);
                ctx.fillStyle = c; ctx.fillRect(px, py, S, 4);
                ctx.fillStyle = '#5a7a4a'; ctx.fillRect(px, py, S, 2);
                for (let i = 0; i < 3; i++) ctx.fillRect(px + (k + i * 5) % 13, py - 1, 1, 2); break;
            }

            case T.BLOODSTONE: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = '#3a0a0a'; ctx.fillRect(px + (k * 3) % 8, py + (k * 5) % 8, 4, 3);
                ctx.fillStyle = '#7a2222'; ctx.fillRect(px + (k * 7) % 10, py + (k * 2) % 10, 3, 2); break;
            }
            case T.LUMINITE: {
                const lg = Math.sin(time / 350 + tx * 2 + ty) * 0.25 + 0.75;
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = `rgba(136,255,221,${lg * 0.4})`; ctx.fillRect(px + 2, py + 2, S - 4, S - 4);
                ctx.fillStyle = `rgba(255,255,255,${lg * 0.2})`; ctx.fillRect(px + 5, py + 5, 4, 3); break;
            }
            case T.SHIMMERSTONE: {
                const shg = Math.sin(time / 400 + tx * 1.5 + ty * 0.5) * 0.2 + 0.8;
                ctx.fillStyle = `rgb(${140 + shg * 30 | 0},${190 + shg * 30 | 0},${220 + shg * 20 | 0})`;
                ctx.fillRect(px, py, S, S);
                ctx.fillStyle = `rgba(200,240,255,${shg * 0.35})`;
                ctx.fillRect(px + 3 + (k % 4), py + 3 + (k % 3), 5, 4);
                ctx.fillStyle = `rgba(255,255,255,${shg * 0.2})`; ctx.fillRect(px + (k * 3) % 10, py + (k * 5) % 10, 2, 1); break;
            }
            // === PHASE 3: NEW BIOME TILES ===
            case T.SPOREGRASS: {
                ctx.fillStyle = '#2a1038'; ctx.fillRect(px, py + 4, S, S - 4);
                ctx.fillStyle = c; ctx.fillRect(px, py, S, 5);
                const spg = Math.sin(time / 350 + tx * 2) * 0.2 + 0.8;
                ctx.fillStyle = `rgba(170,50,200,${spg})`; ctx.fillRect(px, py, S, 2);
                // Tiny spore particles
                ctx.fillStyle = `rgba(200,80,255,${spg * 0.5})`;
                for (let i = 0; i < 2; i++) ctx.fillRect(px + (k + i * 6) % 13, py - 1 - Math.floor(Math.sin(time / 200 + i + tx) * 2), 1, 1); break;
            }
            case T.SPORESTONE: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = '#2a0a38'; ctx.fillRect(px + (k * 3) % 8, py + (k * 5) % 8, 5, 4);
                // Purple veins
                ctx.fillStyle = `rgba(150,40,180,${0.3 + Math.sin(time / 500 + tx + ty) * 0.1})`;
                ctx.fillRect(px + (k * 2) % 10, py, 1, S); ctx.fillRect(px, py + (k * 3) % 10, S, 1); break;
            }
            case T.SPORECAP: {
                const scg = Math.sin(time / 300 + tx * 1.5 + ty) * 0.25 + 0.75;
                ctx.fillStyle = `rgb(${180 + scg * 40 | 0},${40 + scg * 30 | 0},${150 + scg * 40 | 0})`;
                ctx.fillRect(px, py, S, S);
                ctx.fillStyle = `rgba(255,100,220,${scg * 0.4})`; ctx.fillRect(px + 3, py + 3, S - 6, S - 6);
                // Spots
                ctx.fillStyle = '#fff'; ctx.fillRect(px + 3 + (k % 5), py + 3 + (k % 4), 2, 2);
                ctx.fillRect(px + 8 + (k % 4), py + 6 + (k % 3), 2, 2); break;
            }
            case T.PULSEVINE: {
                const pvg = Math.sin(time / 250 + ty * 0.8 + tx * 0.3) * 0.3 + 0.7;
                ctx.fillStyle = `rgba(150,30,180,${pvg})`; ctx.fillRect(px + 5, py, 3, S);
                ctx.fillRect(px + 9, py + 4, 2, S - 4);
                // Glow
                ctx.fillStyle = `rgba(200,80,255,${pvg * 0.4})`; ctx.fillRect(px + 4, py + (k * 3) % 8, 5, 4); break;
            }
            case T.PETRIGRASS: {
                ctx.fillStyle = '#6a6050'; ctx.fillRect(px, py + 4, S, S - 4);
                ctx.fillStyle = c; ctx.fillRect(px, py, S, 5);
                ctx.fillStyle = '#aaa090'; ctx.fillRect(px, py, S, 2);
                // Petrified grass blades (stiff, stone-like)
                ctx.fillStyle = '#8a8070';
                ctx.fillRect(px + 3, py - 1, 1, 2); ctx.fillRect(px + 8, py - 1, 1, 2); ctx.fillRect(px + 12, py, 1, 1); break;
            }
            case T.PETRISTONE: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S);
                // Cracked stone pattern
                ctx.fillStyle = '#6a6050';
                ctx.fillRect(px + (k * 3) % 10, py + (k * 5) % 10, 4, 3);
                ctx.fillStyle = '#5a5040'; ctx.fillRect(px + 2, py + 7, S - 4, 1);
                ctx.fillStyle = '#8a8070'; ctx.fillRect(px + (k * 7) % 12, py + (k * 2) % 8, 2, 2); break;
            }
            case T.FOSSILTREE: {
                ctx.fillStyle = c; ctx.fillRect(px + 4, py, 8, S);
                ctx.fillStyle = '#7a7060'; ctx.fillRect(px + 5, py, 6, S);
                // Ring patterns
                ctx.fillStyle = '#6a6050'; ctx.fillRect(px + 6, py + (k * 3) % 10, 2, 2);
                ctx.fillRect(px + 8, py + (k * 5) % 12, 2, 1); break;
            }
            case T.TIMECRACK: {
                const tcg = Math.sin(time / 200 + tx * 3 + ty * 2) * 0.3 + 0.7;
                ctx.fillStyle = `rgba(220,170,60,${tcg})`; ctx.fillRect(px + 5, py, 2, S);
                ctx.fillStyle = `rgba(255,220,100,${tcg * 0.6})`; ctx.fillRect(px + 4, py + 3, 4, 3);
                ctx.fillStyle = `rgba(255,255,200,${tcg * 0.3})`; ctx.fillRect(px + 3, py + 2, 6, 5);
                // Temporal sparkles
                ctx.fillStyle = `rgba(255,240,150,${tcg * 0.5})`;
                ctx.fillRect(px + 3 + Math.floor(Math.sin(time / 150 + tx) * 3), py + (k * 3) % 10, 1, 1); break;
            }
            case T.PRISMGRASS: {
                ctx.fillStyle = '#5aaa88'; ctx.fillRect(px, py + 4, S, S - 4);
                ctx.fillStyle = c; ctx.fillRect(px, py, S, 5);
                // Rainbow shimmer effect
                const prg = time / 600 + tx * 0.5;
                const pr = 128 + Math.sin(prg) * 60 | 0, pg = 180 + Math.sin(prg + 2) * 40 | 0, pb = 150 + Math.sin(prg + 4) * 50 | 0;
                ctx.fillStyle = `rgb(${pr},${pg},${pb})`; ctx.fillRect(px, py, S, 2);
                ctx.fillStyle = `rgba(255,255,255,0.2)`; ctx.fillRect(px + (k * 3) % 10, py, 4, 1); break;
            }
            case T.PRISMSAND: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S);
                // Sparkling sand with rainbow highlights
                const psg = time / 400 + tx * 0.7;
                ctx.fillStyle = `rgba(${200 + Math.sin(psg) * 55 | 0},${200 + Math.sin(psg + 2) * 55 | 0},${200 + Math.sin(psg + 4) * 55 | 0},0.3)`;
                for (let i = 0; i < 4; i++) ctx.fillRect(px + (k + i * 4) % 14, py + (k * 5 + i * 3) % 14, 1, 1); break;
            }
            case T.GLASSTONE: {
                ctx.fillStyle = 'rgba(190,210,230,0.7)'; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = 'rgba(220,235,250,0.5)'; ctx.fillRect(px + 2, py + 2, S - 4, S - 4);
                ctx.fillStyle = 'rgba(255,255,255,0.3)'; ctx.fillRect(px + 3, py + 3, 3, 2);
                ctx.fillStyle = 'rgba(140,170,200,0.4)'; ctx.fillRect(px, py + 7, S, 1); break;
            }
            case T.PRISMCORE: {
                const pcg = Math.sin(time / 200 + tx * 2 + ty) * 0.2 + 0.8;
                const pcr = 200 + Math.sin(time / 300 + tx) * 55 | 0;
                const pcg2 = 200 + Math.sin(time / 300 + tx + 2.1) * 55 | 0;
                const pcb = 200 + Math.sin(time / 300 + tx + 4.2) * 55 | 0;
                ctx.fillStyle = `rgb(${pcr},${pcg2},${pcb})`; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = `rgba(255,255,255,${pcg * 0.6})`; ctx.fillRect(px + 3, py + 3, S - 6, S - 6);
                ctx.fillStyle = `rgba(255,255,255,${pcg * 0.3})`; ctx.fillRect(px + 1, py + 1, S - 2, S - 2); break;
            }
            case T.GIANTSKULL: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = '#d0c8b0'; ctx.fillRect(px + 1, py + 1, S - 2, S - 2);
                // Cracks
                ctx.fillStyle = '#b8b098'; ctx.fillRect(px + (k * 3) % 10, py, 1, S);
                ctx.fillRect(px, py + (k * 5) % 10, S, 1); break;
            }
            case T.HEARTBLOCK: {
                const hbg = Math.sin(time / 400 + tx + ty * 0.5) * 0.2 + 0.8;
                ctx.fillStyle = `rgb(${150 + hbg * 30 | 0},${10 + hbg * 20 | 0},${50 + hbg * 30 | 0})`;
                ctx.fillRect(px, py, S, S);
                // Pulsing veins
                ctx.fillStyle = `rgba(200,30,80,${hbg * 0.5})`; ctx.fillRect(px + (k * 2) % 8, py, 2, S);
                ctx.fillRect(px, py + (k * 3) % 8, S, 2);
                ctx.fillStyle = `rgba(255,80,120,${hbg * 0.3})`; ctx.fillRect(px + 4, py + 4, S - 8, S - 8); break;
            }
            case T.TITANLIMB: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = '#7a7060'; ctx.fillRect(px + 2 + (k % 4), py + 2 + (k % 3), 4, 3);
                // Ancient markings
                ctx.fillStyle = '#6a6050'; ctx.fillRect(px, py + 8, S, 1);
                ctx.fillRect(px + (k * 5) % 12, py, 1, S); break;
            }
            case T.LIGHTHOUSEBLOCK: {
                const lhg = Math.sin(time / 500 + ty * 0.3) * 0.1 + 0.9;
                ctx.fillStyle = `rgb(${200 + lhg * 30 | 0},${200 + lhg * 30 | 0},${220 + lhg * 25 | 0})`;
                ctx.fillRect(px, py, S, S);
                ctx.fillStyle = `rgba(255,255,255,${lhg * 0.2})`; ctx.fillRect(px + 2, py + 2, S - 4, S - 4);
                ctx.fillStyle = '#c0c0d0'; ctx.fillRect(px, py + 7, S, 1); break;
            }
            // === PHASE 4: UNIQUE BIOME TILES ===
            case T.TIDALROCK: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = '#4a6a6a'; ctx.fillRect(px + (k * 3) % 8, py + (k * 5) % 8, 4, 3);
                // Wet sheen
                const trg = Math.sin(time / 500 + tx * 0.5 + ty * 0.3) * 0.15 + 0.85;
                ctx.fillStyle = `rgba(120,180,200,${trg * 0.2})`; ctx.fillRect(px, py, S, 2);
                ctx.fillStyle = '#6a8a8a'; ctx.fillRect(px + (k * 7) % 12, py + (k * 2) % 10, 2, 2); break;
            }
            case T.BARNACLE: {
                ctx.fillStyle = '#7a8a7a'; ctx.fillRect(px + 3, py + 6, 10, 10);
                ctx.fillStyle = '#9aaa9a'; ctx.fillRect(px + 4, py + 7, 8, 8);
                // Barnacle clusters
                ctx.fillStyle = '#6a7a6a'; ctx.fillRect(px + 5, py + 8, 3, 3);
                ctx.fillRect(px + 9, py + 9, 2, 2);
                ctx.fillStyle = '#b0c0b0'; ctx.fillRect(px + 6, py + 9, 1, 1); break;
            }
            case T.SEAGRASS: {
                const sgSway = Math.sin(time / 400 + tx * 1.5) * 2;
                ctx.fillStyle = '#1a7a4a'; ctx.fillRect(px + 4 + sgSway, py + 2, 2, 14);
                ctx.fillRect(px + 9 + sgSway * 0.7, py + 4, 2, 12);
                ctx.fillStyle = '#2a9a6a'; ctx.fillRect(px + 5 + sgSway * 0.8, py + 3, 1, 8);
                // Tiny bubbles
                const bub = Math.sin(time / 300 + tx * 2) * 0.4 + 0.6;
                ctx.fillStyle = `rgba(150,220,255,${bub * 0.4})`;
                ctx.fillRect(px + 7 + Math.floor(sgSway * 0.5), py + 1, 1, 1); break;
            }
            case T.TIDALSAND: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S);
                // Wet sand with waterlines
                ctx.fillStyle = '#b8a878';
                for (let i = 0; i < 5; i++) ctx.fillRect(px + (k + i * 3) % 14, py + (k * 7 + i * 5) % 14, 1, 1);
                // Water film effect (animated)
                const wf2 = Math.sin(time / 600 + tx * 0.3) * 0.15 + 0.15;
                ctx.fillStyle = `rgba(60,140,200,${wf2})`; ctx.fillRect(px, py, S, S); break;
            }
            case T.DRIFTWOOD: {
                ctx.fillStyle = '#8a7a5a'; ctx.fillRect(px + 4, py, 8, S);
                ctx.fillStyle = '#9a8a6a'; ctx.fillRect(px + 5, py, 6, S);
                // Weathered grain
                ctx.fillStyle = '#7a6a4a'; ctx.fillRect(px + 6, py + (k * 3) % 10, 1, 4);
                ctx.fillRect(px + 8, py + (k * 5) % 8, 1, 3); break;
            }
            case T.STORMSTONE: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = '#3a3a4a'; ctx.fillRect(px + (k * 3) % 8, py + (k * 5) % 8, 5, 4);
                // Lightning cracks
                const stg = Math.sin(time / 300 + tx * 3 + ty * 2) * 0.3 + 0.5;
                ctx.fillStyle = `rgba(140,180,255,${stg * 0.3})`;
                ctx.fillRect(px + (k * 2) % 10, py + 2, 1, S - 4); break;
            }
            case T.STORMGRASS: {
                ctx.fillStyle = '#3a4a3a'; ctx.fillRect(px, py + 4, S, S - 4);
                ctx.fillStyle = c; ctx.fillRect(px, py, S, 5);
                // Wind-swept grass
                const wnd = Math.sin(time / 200 + tx * 2) * 3;
                ctx.fillStyle = '#6a7a5a';
                ctx.fillRect(px + 3 + wnd, py - 1, 1, 3); ctx.fillRect(px + 8 + wnd * 0.7, py - 2, 1, 4);
                ctx.fillRect(px + 12 + wnd * 0.5, py - 1, 1, 2); break;
            }
            case T.CHARGEDCRYSTAL: {
                const ccg = Math.sin(time / 150 + tx * 3 + ty * 2) * 0.3 + 0.7;
                ctx.fillStyle = `rgb(${100 + ccg * 60 | 0},${180 + ccg * 40 | 0},${220 + ccg * 35 | 0})`;
                ctx.fillRect(px + 2, py + 2, S - 4, S - 4);
                ctx.fillStyle = `rgba(160,220,255,${ccg})`; ctx.fillRect(px + 4, py + 4, S - 8, S - 8);
                ctx.fillStyle = `rgba(255,255,255,${ccg * 0.6})`; ctx.fillRect(px + 6, py + 5, 3, 2);
                // Electric sparks
                if (ccg > 0.85) {
                    ctx.fillStyle = '#fff';
                    ctx.fillRect(px + Math.floor(Math.sin(time / 50 + tx) * 4) + 6, py + 3, 1, 2);
                } break;
            }
            case T.OBSIDIANSPIRE: {
                ctx.fillStyle = c; ctx.fillRect(px + 4, py, 8, S);
                ctx.fillStyle = '#2a2a3a'; ctx.fillRect(px + 5, py, 6, S);
                // Obsidian sheen
                ctx.fillStyle = '#3a3a5a'; ctx.fillRect(px + 6, py + (k * 3) % 8, 2, 3); break;
            }
            case T.STORMGLASS: {
                ctx.fillStyle = 'rgba(80,100,120,0.6)'; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = 'rgba(120,150,180,0.4)'; ctx.fillRect(px + 2, py + 2, S - 4, S - 4);
                // Lightning reflection
                const slg = Math.sin(time / 250 + tx * 2) * 0.3 + 0.3;
                ctx.fillStyle = `rgba(180,220,255,${slg})`; ctx.fillRect(px + 3, py + 3, 3, 2); break;
            }
            case T.FLESHSTONE: {
                // Living cave wall that visually pulses
                const flPulse = Math.sin(time / 800 + tx * 0.5 + ty * 0.3) * 0.1 + 0.9;
                ctx.fillStyle = `rgb(${120 * flPulse | 0},${55 * flPulse | 0},${55 * flPulse | 0})`;
                ctx.fillRect(px, py, S, S);
                // Organic texture
                ctx.fillStyle = `rgba(160,70,70,${flPulse * 0.5})`;
                ctx.fillRect(px + (k * 3) % 8, py + (k * 5) % 8, 4, 3);
                // Veins
                ctx.fillStyle = `rgba(200,80,90,${flPulse * 0.4})`;
                ctx.fillRect(px + (k * 2) % 12, py, 1, S); break;
            }
            case T.NERVEVEIN: {
                const nvPulse = Math.sin(time / 300 + tx * 0.8 + ty * 0.5) * 0.3 + 0.7;
                ctx.fillStyle = `rgba(200,80,100,${nvPulse})`; ctx.fillRect(px + 5, py, 3, S);
                ctx.fillRect(px + 8, py + 4, 3, S - 4);
                // Pulse wave traveling through
                const pulsePos = (time / 500 + ty * 0.2) % 1;
                const pulseY = py + pulsePos * S;
                ctx.fillStyle = `rgba(255,120,140,${nvPulse * 0.7})`; ctx.fillRect(px + 4, pulseY, 5, 3);
                // Glow
                ctx.fillStyle = `rgba(255,150,170,${nvPulse * 0.3})`; ctx.fillRect(px + 3, py + (k * 3) % 8, 7, 4); break;
            }
            case T.PULSEWALL: {
                const pwPulse = Math.sin(time / 600 + tx * 0.4 + ty * 0.6) * 0.15 + 0.85;
                ctx.fillStyle = `rgb(${90 * pwPulse | 0},${42 * pwPulse | 0},${42 * pwPulse | 0})`;
                ctx.fillRect(px, py, S, S);
                ctx.fillStyle = `rgba(120,50,60,${pwPulse * 0.4})`;
                ctx.fillRect(px + 2 + (k % 4), py + 2 + (k % 3), 4, 3);
                // Subtle breathing motion via brightness
                ctx.fillStyle = `rgba(150,60,70,${(pwPulse - 0.7) * 0.8})`;
                ctx.fillRect(px, py, S, S); break;
            }
            case T.ORGANICMOSS: {
                const omPulse = Math.sin(time / 500 + tx * 0.7) * 0.2 + 0.8;
                ctx.fillStyle = `rgba(100,70,55,${omPulse})`; ctx.fillRect(px + 3, py + 6, 10, 10);
                ctx.fillStyle = `rgba(120,85,68,${omPulse})`; ctx.fillRect(px + 4, py + 8, 8, 6);
                // Tiny organic tendrils
                ctx.fillStyle = `rgba(150,90,70,${omPulse * 0.7})`;
                ctx.fillRect(px + 5, py + 4, 2, 4); ctx.fillRect(px + 9, py + 5, 2, 3); break;
            }
            case T.EYEBLOCK: {
                const eyePulse = Math.sin(time / 400 + tx + ty * 0.5) * 0.2 + 0.8;
                // Surrounding flesh
                ctx.fillStyle = `rgb(${140 * eyePulse | 0},${60 * eyePulse | 0},${60 * eyePulse | 0})`;
                ctx.fillRect(px, py, S, S);
                // Eye white
                ctx.fillStyle = `rgba(230,220,210,${eyePulse})`;
                ctx.fillRect(px + 3, py + 4, 10, 8);
                // Iris — follows a slow cycle
                const eyeOff = Math.sin(time / 2000 + tx * 0.5) * 2;
                ctx.fillStyle = `rgba(180,40,40,${eyePulse})`;
                ctx.fillRect(px + 6 + eyeOff, py + 6, 4, 4);
                // Pupil
                ctx.fillStyle = `rgba(20,0,0,${eyePulse})`;
                ctx.fillRect(px + 7 + eyeOff, py + 7, 2, 2);
                // Highlight
                ctx.fillStyle = `rgba(255,255,255,${eyePulse * 0.6})`;
                ctx.fillRect(px + 7 + eyeOff, py + 6, 1, 1); break;
            }
            case T.WORLDCRACK: {
                // Pulsing purple crack
                const crackPulse = Math.sin(time / 400 + tx * 0.5 + ty * 0.3) * 0.3 + 0.7;
                ctx.fillStyle = `rgb(${Math.floor(42 * crackPulse)},${Math.floor(10 * crackPulse)},${Math.floor(58 * crackPulse)})`;
                ctx.fillRect(px, py, S, S);
                // Glowing veins
                ctx.fillStyle = `rgba(180,80,255,${crackPulse * 0.5})`;
                ctx.fillRect(px + 6, py, 2, S);
                ctx.fillRect(px + 4, py + 4, 6, 2);
                // Energy particles
                ctx.fillStyle = `rgba(200,120,255,${crackPulse * 0.7})`;
                ctx.fillRect(px + 7, py + Math.floor(Math.sin(time / 200 + ty) * 3) + 6, 2, 2);
                break;
            }
            case T.WORLDROOT: {
                // Rich wood grain
                const rootK = k % 4;
                ctx.fillStyle = '#5a3a1a'; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = '#4a2a10';
                ctx.fillRect(px + rootK * 3, py, 2, S);
                ctx.fillRect(px + rootK * 3 + 7, py, 1, S);
                ctx.fillStyle = '#6a4a22';
                ctx.fillRect(px, py + rootK * 4, S, 2);
                // Sap specks
                if (k > 5) { ctx.fillStyle = 'rgba(220,170,34,0.4)'; ctx.fillRect(px + 4 + rootK, py + 5 + rootK, 2, 2); }
                break;
            }
            case T.WORLDBARK: {
                // Rough outer bark
                ctx.fillStyle = '#3a2a10'; ctx.fillRect(px, py, S, S);
                // Bark ridges
                ctx.fillStyle = '#2a1a08';
                ctx.fillRect(px + k % 3 * 4, py, 3, S);
                ctx.fillRect(px + (k + 2) % 4 * 3 + 2, py, 2, S);
                ctx.fillStyle = '#4a3518';
                ctx.fillRect(px, py + k % 2 * 6 + 2, S, 1);
                // Moss patches
                if (k > 4) {
                    ctx.fillStyle = '#1a6622'; ctx.fillRect(px + k % 5 * 2, py + k % 3 * 4, 4, 3);
                }
                break;
            }
            case T.WORLDHEART: {
                // Pulsing red heart — strongest glow
                const heartBeat = Math.sin(time / 300) * 0.4 + 0.6;
                ctx.fillStyle = `rgb(${Math.floor(255 * heartBeat)},${Math.floor(34 * heartBeat)},${Math.floor(100 * heartBeat)})`;
                ctx.fillRect(px, py, S, S);
                // Inner glow
                ctx.fillStyle = `rgba(255,150,200,${heartBeat * 0.5})`;
                ctx.fillRect(px + 3, py + 3, S - 6, S - 6);
                // Veins
                ctx.fillStyle = `rgba(200,20,60,${heartBeat})`;
                ctx.fillRect(px + 2, py + S / 2, S - 4, 1);
                ctx.fillRect(px + S / 2, py + 2, 1, S - 4);
                // Sparkle
                ctx.fillStyle = `rgba(255,255,255,${heartBeat * 0.3})`;
                ctx.fillRect(px + 4, py + 4, 2, 2);
                break;
            }
            case T.WORLDLEAF: {
                // Lush green leaves with depth
                const leafK = k % 3;
                const leafWave = Math.sin(time / 800 + tx * 0.3 + ty * 0.2) * 0.1;
                const lr = Math.floor(34 + leafWave * 50);
                const lg = Math.floor(170 + leafWave * 40);
                const lb = Math.floor(68 + leafWave * 20);
                ctx.fillStyle = `rgb(${lr},${lg},${lb})`;
                ctx.fillRect(px, py, S, S);
                // Leaf vein pattern
                ctx.fillStyle = `rgb(${lr - 15},${lg + 10},${lb - 10})`;
                ctx.fillRect(px + 7, py, 1, S);
                ctx.fillRect(px, py + 7, S, 1);
                // Light dapples
                if (leafK === 0) { ctx.fillStyle = `rgba(200,255,200,0.25)`; ctx.fillRect(px + 2, py + 2, 5, 4); }
                // Dark spots for depth
                if (leafK === 2) { ctx.fillStyle = 'rgba(0,60,0,0.2)'; ctx.fillRect(px + k * 2 % 10, py + k * 3 % 12, 3, 3); }
                break;
            }
            case T.WORLDMOSS: {
                // Hanging moss / vine
                const mossWave = Math.sin(time / 600 + tx * 0.7 + ty * 0.5) * 2;
                ctx.fillStyle = '#1a8833';
                ctx.fillRect(px + 5 + Math.floor(mossWave), py, 4, S);
                ctx.fillStyle = '#22aa44';
                ctx.fillRect(px + 6 + Math.floor(mossWave), py, 2, S);
                // Droplet at bottom
                if (k > 5) { ctx.fillStyle = '#88ffaa'; ctx.fillRect(px + 6, py + S - 2, 2, 2); }
                break;
            }
            case T.ROOTVEIN: {
                // Thin glowing root
                const veinPulse = Math.sin(time / 500 + tx * 0.3 + ty * 0.4) * 0.3 + 0.7;
                ctx.fillStyle = `rgba(136,204,85,${veinPulse * 0.8})`;
                ctx.fillRect(px + 5, py + 2, 4, S - 4);
                ctx.fillStyle = `rgba(170,255,130,${veinPulse * 0.5})`;
                ctx.fillRect(px + 6, py + 3, 2, S - 6);
                // Glow
                ctx.fillStyle = `rgba(136,204,85,${veinPulse * 0.15})`;
                ctx.fillRect(px, py, S, S);
                break;
            }
            case T.WORLDSAP: {
                // Glowing amber sap — light source
                const sapPulse = Math.sin(time / 300 + tx + ty) * 0.2 + 0.8;
                ctx.fillStyle = `rgba(220,170,34,${sapPulse})`;
                ctx.fillRect(px + 4, py + 3, S - 8, S - 6);
                ctx.fillStyle = `rgba(255,238,136,${sapPulse * 0.8})`;
                ctx.fillRect(px + 5, py + 4, S - 10, S - 8);
                // Glow around
                ctx.fillStyle = `rgba(220,170,34,${sapPulse * 0.2})`;
                ctx.fillRect(px - 2, py - 2, S + 4, S + 4);
                break;
            }
            case T.WORLDCORE: {
                // Crystalline core material
                const corePulse = Math.sin(time / 400 + tx * 0.5) * 0.3 + 0.7;
                ctx.fillStyle = `rgb(${Math.floor(255 * corePulse)},${Math.floor(204 * corePulse)},${Math.floor(68 * corePulse)})`;
                ctx.fillRect(px, py, S, S);
                ctx.fillStyle = `rgba(255,255,200,${corePulse * 0.4})`;
                ctx.fillRect(px + 3, py + 3, S - 6, S - 6);
                break;
            }
            case T.TREEHOME: {
                // Polished wood floor
                ctx.fillStyle = '#6a4a20'; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = '#7a5a28'; ctx.fillRect(px, py, S, 2);
                ctx.fillStyle = '#5a3a18'; ctx.fillRect(px + k * 3 % 12, py, 2, S);
                break;
            }
            case T.TREEPLATFORM: {
                // Semi-solid platform — player can jump through from below
                ctx.fillStyle = '#8a6a30'; ctx.fillRect(px, py, S, 5);
                ctx.fillStyle = '#6a5020'; ctx.fillRect(px, py + 3, S, 2);
                ctx.fillStyle = '#9a7a38'; ctx.fillRect(px + 2, py, S - 4, 1);
                break;
            }
            case T.ROOTCRYSTAL: {
                // Luminescent green crystal
                const rcPulse = Math.sin(time / 500 + tx + ty * 0.7) * 0.3 + 0.7;
                ctx.fillStyle = `rgba(170,255,204,${rcPulse})`;
                ctx.fillRect(px + 3, py + 2, S - 6, S - 4);
                ctx.fillStyle = `rgba(200,255,230,${rcPulse * 0.8})`;
                ctx.fillRect(px + 5, py + 4, S - 10, S - 8);
                // Facets
                ctx.fillStyle = `rgba(255,255,255,${rcPulse * 0.3})`;
                ctx.fillRect(px + 4, py + 3, 2, 3);
                // Glow
                ctx.fillStyle = `rgba(170,255,204,${rcPulse * 0.15})`;
                ctx.fillRect(px - 2, py - 2, S + 4, S + 4);
                break;
            }
            case T.CRACKSHARD: {
                // Purple crystal shard
                const csPulse = Math.sin(time / 350 + tx * 0.8 + ty * 0.6) * 0.3 + 0.7;
                ctx.fillStyle = `rgba(153,68,221,${csPulse})`;
                ctx.fillRect(px + 5, py + 1, 4, S - 2);
                ctx.fillStyle = `rgba(187,102,238,${csPulse * 0.7})`;
                ctx.fillRect(px + 6, py + 3, 2, S - 6);
                ctx.fillStyle = `rgba(153,68,221,${csPulse * 0.15})`;
                ctx.fillRect(px, py, S, S);
                break;
            }
            case T.MUTATEDSTONE: {
                // Dark purple stone with veins
                ctx.fillStyle = '#5a3a6a'; ctx.fillRect(px, py, S, S);
                ctx.fillStyle = '#4a2a5a'; ctx.fillRect(px + k % 3 * 5, py + k % 2 * 7, 4, 4);
                ctx.fillStyle = 'rgba(150,80,200,0.3)'; ctx.fillRect(px + 3, py + 6, S - 6, 1);
                ctx.fillStyle = darker; ctx.fillRect(px, py + S - 2, S, 2);
                ctx.fillStyle = lighter; ctx.fillRect(px, py, S, 1);
                break;
            }
            // === VILLAGE TILE RENDERS ===
            case T.PLANK: {
                ctx.fillStyle = '#b08050'; ctx.fillRect(px, py, S, S);
                // Wood grain
                ctx.fillStyle = '#9a7040'; ctx.fillRect(px, py + 3, S, 1); ctx.fillRect(px, py + 7, S, 1); ctx.fillRect(px, py + 11, S, 1);
                // Nail dots
                ctx.fillStyle = '#666'; ctx.fillRect(px + 2, py + 1, 1, 1); ctx.fillRect(px + 12, py + 5, 1, 1);
                ctx.fillStyle = '#c09060'; ctx.fillRect(px, py, S, 1); break;
            }
            case T.GLASS: {
                ctx.fillStyle = 'rgba(150,200,240,0.4)'; ctx.fillRect(px, py, S, S);
                // Frame
                ctx.fillStyle = '#6a5030'; ctx.fillRect(px, py, S, 1); ctx.fillRect(px, py + S - 1, S, 1);
                ctx.fillRect(px, py, 1, S); ctx.fillRect(px + S - 1, py, 1, S);
                // Cross frame
                ctx.fillRect(px + S / 2, py, 1, S); ctx.fillRect(px, py + S / 2, S, 1);
                // Glint
                ctx.fillStyle = 'rgba(255,255,255,0.4)'; ctx.fillRect(px + 3, py + 2, 3, 2); break;
            }
            case T.FENCE: {
                ctx.fillStyle = '#9a7040';
                // Vertical posts
                ctx.fillRect(px + 2, py, 2, S); ctx.fillRect(px + S - 4, py, 2, S);
                // Horizontal rails
                ctx.fillStyle = '#8a6030'; ctx.fillRect(px, py + 3, S, 2); ctx.fillRect(px, py + S - 5, S, 2);
                // Post tops
                ctx.fillStyle = '#aa8050'; ctx.fillRect(px + 2, py, 2, 1); ctx.fillRect(px + S - 4, py, 2, 1); break;
            }
            case T.BOOKSHELF: {
                ctx.fillStyle = '#6a4020'; ctx.fillRect(px, py, S, S);
                // Shelves
                ctx.fillStyle = '#5a3018'; ctx.fillRect(px, py + 5, S, 1); ctx.fillRect(px, py + 10, S, 1);
                // Books (colored spines)
                const bookCols = ['#cc3333', '#3366cc', '#33aa33', '#cc9933', '#9933cc', '#cc6633'];
                for (let i = 0; i < 5; i++) {
                    ctx.fillStyle = bookCols[(k + i) % bookCols.length];
                    ctx.fillRect(px + 1 + i * 3, py + 1, 2, 4);
                    ctx.fillRect(px + 1 + i * 3, py + 6, 2, 4);
                }
                // Shelf edge highlight
                ctx.fillStyle = '#7a5028'; ctx.fillRect(px, py, S, 1); break;
            }
            case T.BARREL: {
                ctx.fillStyle = '#7a5a30'; ctx.fillRect(px + 2, py + 1, S - 4, S - 2);
                // Barrel staves
                ctx.fillStyle = '#6a4a20'; ctx.fillRect(px + 3, py + 1, 1, S - 2); ctx.fillRect(px + S - 4, py + 1, 1, S - 2);
                // Metal hoops
                ctx.fillStyle = '#888'; ctx.fillRect(px + 1, py + 3, S - 2, 1); ctx.fillRect(px + 1, py + S - 4, S - 2, 1);
                // Top
                ctx.fillStyle = '#8a6a38'; ctx.fillRect(px + 3, py, S - 6, 2);
                // Highlight
                ctx.fillStyle = '#9a7a48'; ctx.fillRect(px + 5, py + 4, 2, 3); break;
            }
            case T.HAY: {
                ctx.fillStyle = '#d4b848'; ctx.fillRect(px, py, S, S);
                // Straw texture
                ctx.fillStyle = '#c4a838'; for (let i = 0; i < 6; i++) ctx.fillRect(px + (k + i * 3) % 14, py + i * 2, 2, 3);
                ctx.fillStyle = '#e4c858'; for (let i = 0; i < 4; i++) ctx.fillRect(px + (k + i * 4) % 12, py + i * 3 + 1, 1, 4);
                // Binding
                ctx.fillStyle = '#8a6a20'; ctx.fillRect(px, py + 7, S, 1); break;
            }
            case T.COBBLEPATH: {
                ctx.fillStyle = '#8a8a80'; ctx.fillRect(px, py, S, S);
                // Individual stones
                ctx.fillStyle = '#7a7a70'; ctx.fillRect(px + 1, py + 1, 6, 5); ctx.fillRect(px + 8, py + 2, 6, 4);
                ctx.fillRect(px + 2, py + 7, 5, 5); ctx.fillRect(px + 9, py + 8, 5, 6);
                // Mortar lines
                ctx.fillStyle = '#6a6a60'; ctx.fillRect(px + 7, py, 1, S); ctx.fillRect(px, py + 6, S, 1);
                // Highlight
                ctx.fillStyle = '#9a9a90'; ctx.fillRect(px + 2, py + 2, 3, 2); ctx.fillRect(px + 10, py + 9, 2, 2); break;
            }
            case T.CHIMNEY: {
                ctx.fillStyle = '#5a4a4a'; ctx.fillRect(px + 3, py, S - 6, S);
                // Bricks pattern
                ctx.fillStyle = '#4a3a3a'; ctx.fillRect(px + 3, py + 4, S - 6, 1); ctx.fillRect(px + 3, py + 8, S - 6, 1);
                ctx.fillRect(px + 7, py, 1, 4); ctx.fillRect(px + 5, py + 4, 1, 4);
                // Soot at top
                ctx.fillStyle = '#333'; ctx.fillRect(px + 4, py, S - 8, 2);
                // Smoke particles (subtle)
                const smk = Math.sin(time / 300 + tx) * 0.2 + 0.3;
                ctx.fillStyle = `rgba(100,100,100,${smk})`; ctx.fillRect(px + 5 + Math.floor(Math.sin(time / 200 + ty) * 2), py - 2, 3, 2); break;
            }
            case T.FLOWERPOT: {
                // Pot
                ctx.fillStyle = '#b06030'; ctx.fillRect(px + 4, py + 8, 8, 8);
                ctx.fillStyle = '#a05020'; ctx.fillRect(px + 5, py + 9, 6, 6);
                ctx.fillStyle = '#c07040'; ctx.fillRect(px + 3, py + 7, 10, 2);
                // Dirt
                ctx.fillStyle = '#6a4a20'; ctx.fillRect(px + 5, py + 7, 6, 2);
                // Flower
                const fCols = ['#ff4466', '#ffdd44', '#ff88cc', '#4488ff', '#44cc44'];
                ctx.fillStyle = fCols[(k + tx) % fCols.length];
                ctx.fillRect(px + 6, py + 2, 4, 4);
                // Stem
                ctx.fillStyle = '#2a8a2a'; ctx.fillRect(px + 7, py + 5, 2, 3); break;
            }
            case T.VDOOR: {
                ctx.fillStyle = '#6a4a28'; ctx.fillRect(px + 2, py, S - 4, S);
                // Door panels
                ctx.fillStyle = '#5a3a18'; ctx.fillRect(px + 3, py + 1, S - 6, 6); ctx.fillRect(px + 3, py + 9, S - 6, 6);
                // Door knob
                ctx.fillStyle = '#ccaa44'; ctx.fillRect(px + S - 5, py + 7, 2, 2);
                // Frame
                ctx.fillStyle = '#7a5a38'; ctx.fillRect(px + 1, py, 1, S); ctx.fillRect(px + S - 2, py, 1, S); break;
            }
            case T.VILLSIGN: {
                // Sign post
                ctx.fillStyle = '#6a4a2a'; ctx.fillRect(px + 7, py + 6, 2, 10);
                // Sign board
                ctx.fillStyle = '#8a6a3a'; ctx.fillRect(px + 1, py + 2, 14, 8);
                ctx.fillStyle = '#7a5a2a'; ctx.fillRect(px + 2, py + 3, 12, 6);
                // Text marks
                ctx.fillStyle = '#4a3a1a'; ctx.fillRect(px + 4, py + 5, 3, 1); ctx.fillRect(px + 8, py + 5, 4, 1); ctx.fillRect(px + 5, py + 7, 5, 1); break;
            }
            case T.THATCH: {
                ctx.fillStyle = '#c0a040'; ctx.fillRect(px, py, S, S);
                // Thatch pattern
                ctx.fillStyle = '#b09030'; for (let i = 0; i < 6; i++) ctx.fillRect(px + (k + i * 3) % 14, py + i * 2, 3, 2);
                ctx.fillStyle = '#d0b050'; for (let i = 0; i < 4; i++) ctx.fillRect(px + (k + i * 5) % 12, py + i * 3, 1, 4);
                ctx.fillStyle = '#a08020'; ctx.fillRect(px, py + S - 2, S, 2); break;
            }
            case T.CLAYBRICK: {
                ctx.fillStyle = '#c08050'; ctx.fillRect(px, py, S, S);
                // Brick pattern
                ctx.fillStyle = '#b07040'; ctx.fillRect(px, py + 4, S, 1); ctx.fillRect(px, py + 8, S, 1); ctx.fillRect(px, py + 12, S, 1);
                ctx.fillRect(px + 7, py, 1, 4); ctx.fillRect(px + 3, py + 4, 1, 4); ctx.fillRect(px + 11, py + 8, 1, 4);
                ctx.fillStyle = '#d09060'; ctx.fillRect(px + 1, py + 1, 5, 3); break;
            }
            case T.BENCH: {
                // Seat
                ctx.fillStyle = '#8a6a3a'; ctx.fillRect(px, py + 8, S, 3);
                ctx.fillStyle = '#9a7a48'; ctx.fillRect(px + 1, py + 8, S - 2, 1);
                // Legs
                ctx.fillStyle = '#6a4a2a'; ctx.fillRect(px + 1, py + 11, 2, 5); ctx.fillRect(px + S - 3, py + 11, 2, 5);
                // Back rest
                ctx.fillStyle = '#7a5a30'; ctx.fillRect(px + 1, py + 3, S - 2, 2);
                ctx.fillStyle = '#6a4a28'; ctx.fillRect(px + 1, py + 5, 1, 3); ctx.fillRect(px + S - 2, py + 5, 1, 3); break;
            }
            case T.WELLROOF: {
                ctx.fillStyle = '#7a5a2a'; ctx.fillRect(px, py, S, 4);
                ctx.fillStyle = '#6a4a1a'; ctx.fillRect(px, py + 2, S, 2);
                ctx.fillStyle = '#8a6a38'; ctx.fillRect(px, py, S, 1); break;
            }
            case T.STOVE: {
                ctx.fillStyle = '#4a3a3a'; ctx.fillRect(px + 1, py + 2, S - 2, S - 2);
                ctx.fillStyle = '#3a2a2a'; ctx.fillRect(px + 2, py + 3, S - 4, S - 4);
                // Fire glow inside
                const stGlow = Math.sin(time / 200 + tx) * 0.2 + 0.6;
                ctx.fillStyle = `rgba(255,120,30,${stGlow})`; ctx.fillRect(px + 4, py + 6, S - 8, S - 8);
                // Top plate
                ctx.fillStyle = '#5a4a4a'; ctx.fillRect(px, py + 1, S, 2); break;
            }
            case T.CRATE: {
                ctx.fillStyle = '#9a7a40'; ctx.fillRect(px + 1, py + 1, S - 2, S - 2);
                // Boards
                ctx.fillStyle = '#8a6a30'; ctx.fillRect(px + 1, py + 5, S - 2, 1); ctx.fillRect(px + 1, py + 10, S - 2, 1);
                ctx.fillRect(px + 7, py + 1, 1, S - 2);
                // Corner braces
                ctx.fillStyle = '#6a5a28'; ctx.fillRect(px + 1, py + 1, 2, 2); ctx.fillRect(px + S - 3, py + 1, 2, 2);
                ctx.fillRect(px + 1, py + S - 3, 2, 2); ctx.fillRect(px + S - 3, py + S - 3, 2, 2); break;
            }
            case T.ROOFTILE: {
                ctx.fillStyle = '#aa5533'; ctx.fillRect(px, py, S, S);
                // Tile pattern
                ctx.fillStyle = '#994422'; ctx.fillRect(px, py + 4, S, 2); ctx.fillRect(px, py + 10, S, 2);
                ctx.fillRect(px + 6, py, 1, 4); ctx.fillRect(px + 2, py + 4, 1, 6); ctx.fillRect(px + 10, py + 10, 1, 6);
                ctx.fillStyle = '#bb6644'; ctx.fillRect(px + 1, py + 1, 4, 2); break;
            }
            case T.CANOPY: {
                const canP = Math.sin(time / 600 + tx * 0.5) * 0.1 + 0.9;
                ctx.fillStyle = `rgb(${34 + canP * 10 | 0},${100 + canP * 22 | 0},${48 + canP * 10 | 0})`;
                ctx.fillRect(px, py, S, S);
                ctx.fillStyle = '#1a5a28'; ctx.fillRect(px + (k * 3) % 8, py + (k * 5) % 8, 4, 3);
                ctx.fillStyle = '#3a9a4a'; ctx.fillRect(px + (k * 7) % 10, py + (k * 2) % 6, 3, 2); break;
            }
            case T.ARCHWAY: {
                ctx.fillStyle = '#aaa090'; ctx.fillRect(px, py + 4, S, S - 4);
                // Arch shape (clear top center)
                ctx.fillStyle = '#bbb0a0'; ctx.fillRect(px + 2, py, S - 4, 5);
                ctx.fillStyle = '#9a9080'; ctx.fillRect(px, py + 2, 3, S - 2); ctx.fillRect(px + S - 3, py + 2, 3, S - 2);
                // Keystone
                ctx.fillStyle = '#c0b098'; ctx.fillRect(px + 6, py, 4, 3); break;
            }
            case T.WINDOWBOX: {
                // Wooden box
                ctx.fillStyle = '#8a4a2a'; ctx.fillRect(px + 1, py + 10, S - 2, 6);
                ctx.fillStyle = '#7a3a1a'; ctx.fillRect(px + 2, py + 11, S - 4, 4);
                // Dirt
                ctx.fillStyle = '#5a3a10'; ctx.fillRect(px + 2, py + 10, S - 4, 2);
                // Flowers
                const wbCols = ['#ff5566', '#ffcc33', '#ff88bb'];
                ctx.fillStyle = wbCols[k % 3]; ctx.fillRect(px + 3, py + 6, 3, 4); ctx.fillRect(px + 9, py + 7, 3, 3);
                ctx.fillStyle = '#2a7a2a'; ctx.fillRect(px + 4, py + 8, 1, 3); ctx.fillRect(px + 10, py + 9, 1, 2); break;
            }
            case T.CARPET: {
                ctx.fillStyle = '#8a2a3a'; ctx.fillRect(px, py, S, S);
                // Pattern
                ctx.fillStyle = '#aa3a4a'; ctx.fillRect(px + 2, py + 2, S - 4, S - 4);
                // Border design
                ctx.fillStyle = '#cc8833'; ctx.fillRect(px, py, S, 1); ctx.fillRect(px, py + S - 1, S, 1);
                // Inner motif
                ctx.fillStyle = '#7a1a2a'; ctx.fillRect(px + 5, py + 5, S - 10, S - 10); break;
            }
            case T.BELLTOWER: {
                ctx.fillStyle = '#b0a080'; ctx.fillRect(px + 4, py + 2, S - 8, S - 4);
                // Bell shape
                ctx.fillStyle = '#ccaa44'; ctx.fillRect(px + 5, py + 4, S - 10, S - 6);
                ctx.fillStyle = '#ddbb55'; ctx.fillRect(px + 6, py + 5, S - 12, S - 8);
                // Clapper
                ctx.fillStyle = '#8a7a40'; ctx.fillRect(px + 7, py + S - 3, 2, 2); break;
            }
            case T.VPILLAR: {
                ctx.fillStyle = '#a09070'; ctx.fillRect(px + 4, py, S - 8, S);
                ctx.fillStyle = '#b0a080'; ctx.fillRect(px + 5, py, S - 10, S);
                // Base/cap
                ctx.fillStyle = '#908060'; ctx.fillRect(px + 3, py, S - 6, 2); ctx.fillRect(px + 3, py + S - 2, S - 6, 2); break;
            }
            default: {
                ctx.fillStyle = c; ctx.fillRect(px, py, S, S); ctx.fillStyle = darker; ctx.fillRect(px, py + S - 2, S, 2); ctx.fillRect(px + S - 2, py, 2, S);
                ctx.fillStyle = lighter; ctx.fillRect(px, py, S, 1); ctx.fillRect(px, py, 1, S); break;
            }
        }
    }
}

// === PARALLAX — Enhanced with biome-specific details ===
export class ParallaxBG {
    constructor(seed) { 
        this.perlins = []; 
        for (let i = 0; i < 4; i++) this.perlins.push(new Perlin(seed + 200 + i)); 
        this.smoothBaseY = null; 
        this.smoothCols = [null, null, null];
        this.detailNoise = new Perlin(seed + 999); // For biome details
        this.cloudNoise = new Perlin(seed + 777); // For world-space clouds
    }
    render(ctx, camX, camY, vw, vh, biome, surfY, time, biomeWeights) {
        const targetBaseY = surfY * TS;
        if (this.smoothBaseY === null) this.smoothBaseY = targetBaseY;
        this.smoothBaseY += (targetBaseY - this.smoothBaseY) * 0.008;
        const baseY = this.smoothBaseY;
        const cols = this.getBlendedColors(biomeWeights || { [biome]: 1 });
        const layers = [{ spd: 0.04, sc: 0.004, amp: 50, base: 55, n: this.perlins[0], col: cols[0] },
        { spd: 0.12, sc: 0.01, amp: 35, base: 35, n: this.perlins[1], col: cols[1] },
        { spd: 0.25, sc: 0.018, amp: 22, base: 18, n: this.perlins[2], col: cols[2] }];
        const screenL = camX - vw / 2 - 10, screenR = camX + vw / 2 + 10, screenBot = camY + vh / 2 + 50;
        // Determine dominant biome for tree style
        let domBiome = biome;
        if (biomeWeights) { let mw = 0; for (const b in biomeWeights) if (biomeWeights[b] > mw) { mw = biomeWeights[b]; domBiome = parseInt(b); } }
        
        // Render background layers with biome-specific mountains/features
        for (let li = 0; li < layers.length; li++) {
            const L = layers[li]; const yOff = Math.round((camY - baseY) * L.spd * 0.12);
            
            // Biome-specific background details (only on furthest layer to save performance)
            if (li === 0) this.drawBiomeBackdrop(ctx, camX, baseY, yOff, screenL, screenR, domBiome, L, time);
            
            ctx.fillStyle = L.col; ctx.beginPath(); ctx.moveTo(screenL, screenBot);
            for (let px = screenL; px <= screenR; px += 3) {
                const lx = (px - camX * (1 - L.spd)) * L.sc;
                const h2 = L.n.oct(lx, li * 10, 3, 0.5, 2) * L.amp + L.base; ctx.lineTo(px, baseY - h2 - yOff);
            }
            ctx.lineTo(screenR, screenBot); ctx.closePath(); ctx.fill();
            if (li === 1 && ![5, 6, 13, 17, 26, 27, 28, 30].includes(domBiome)) this.drawTrees(ctx, L, camX, screenL, screenR, baseY, yOff, domBiome);
        }
    }
    drawBiomeBackdrop(ctx, camX, baseY, yOff, screenL, screenR, biome, L, time) {
        const spd = 0.02; // Even slower parallax for distant features
        const spacing = 180;
        const visL = camX * spd - 500, visR = camX * spd + 500;
        const first = Math.floor(visL / spacing) * spacing;
        
        for (let fx = first; fx <= visR; fx += spacing) {
            const drawX = fx + camX * (1 - spd);
            if (drawX < screenL - 100 || drawX > screenR + 100) continue;
            const hash = ((fx * 7919) >>> 0) % 100;
            
            switch(biome) {
                case 0: case 18: // Tundra/Frozen Shore - distant ice peaks
                    if (hash > 60) {
                        const h = 80 + hash % 50;
                        ctx.fillStyle = 'rgba(200,220,240,0.15)';
                        ctx.beginPath();
                        ctx.moveTo(drawX - 40, baseY - yOff);
                        ctx.lineTo(drawX, baseY - h - yOff);
                        ctx.lineTo(drawX + 40, baseY - yOff);
                        ctx.fill();
                    }
                    break;
                case 13: case 21: // Volcanic/Ember - distant volcanoes with glow
                    if (hash > 70) {
                        const h = 60 + hash % 40;
                        const glow = Math.sin(time / 500 + fx * 0.01) * 0.1 + 0.2;
                        ctx.fillStyle = `rgba(100,30,10,${glow})`;
                        ctx.beginPath();
                        ctx.moveTo(drawX - 35, baseY - yOff);
                        ctx.lineTo(drawX, baseY - h - yOff);
                        ctx.lineTo(drawX + 35, baseY - yOff);
                        ctx.fill();
                        // Lava glow on top
                        ctx.fillStyle = `rgba(255,100,30,${glow * 0.5})`;
                        ctx.fillRect(drawX - 8, baseY - h - yOff, 16, 3);
                    }
                    break;
                case 19: // Crystal Caverns - floating crystal formations
                    if (hash > 50) {
                        const sz = 12 + hash % 15;
                        const bob = Math.sin(time / 800 + fx * 0.02) * 6;
                        const glow = Math.sin(time / 400 + fx * 0.03) * 0.15 + 0.25;
                        ctx.fillStyle = `rgba(140,160,230,${glow})`;
                        ctx.beginPath();
                        ctx.moveTo(drawX, baseY - 80 - yOff + bob);
                        ctx.lineTo(drawX - sz/2, baseY - 80 - yOff - sz + bob);
                        ctx.lineTo(drawX + sz/2, baseY - 80 - yOff - sz + bob);
                        ctx.fill();
                    }
                    break;
                case 20: // Abyss - bioluminescent jellyfish silhouettes
                    if (hash > 55) {
                        const sz = 10 + hash % 12;
                        const drift = Math.sin(time / 1000 + fx * 0.015) * 20;
                        const pulse = Math.sin(time / 300 + fx * 0.02) * 0.2 + 0.2;
                        ctx.fillStyle = `rgba(50,150,180,${pulse})`;
                        ctx.beginPath();
                        ctx.arc(drawX + drift, baseY - 100 - yOff, sz, 0, Math.PI, true);
                        ctx.fill();
                        // Tentacles
                        for (let i = 0; i < 3; i++) {
                            const tx = drawX + drift + (i - 1) * sz/2;
                            ctx.fillRect(tx, baseY - 100 - yOff, 2, 15);
                        }
                    }
                    break;
                case 27: // Prism Shores - rainbow auroras
                    if (hash > 40) {
                        const w = 60 + hash % 40;
                        const offset = time / 600 + fx * 0.01;
                        const r = 128 + Math.sin(offset) * 80 | 0;
                        const g = 128 + Math.sin(offset + 2) * 80 | 0;
                        const b = 128 + Math.sin(offset + 4) * 80 | 0;
                        ctx.fillStyle = `rgba(${r},${g},${b},0.08)`;
                        ctx.fillRect(drawX - w/2, baseY - 120 - yOff, w, 60);
                    }
                    break;
                case 29: // Storm Peaks - lightning flashes
                    if (hash > 85 && Math.sin(time / 100 + fx) > 0.95) {
                        ctx.fillStyle = 'rgba(200,220,255,0.4)';
                        ctx.fillRect(drawX - 2, baseY - 150 - yOff, 4, 80);
                        ctx.fillRect(drawX - 15, baseY - 90 - yOff, 30, 3);
                    }
                    break;
                case 25: // Fungal Abyss - giant spore clouds
                    if (hash > 60) {
                        const sz = 30 + hash % 25;
                        const drift = Math.sin(time / 1200 + fx * 0.01) * 15;
                        const alpha = Math.sin(time / 500 + fx * 0.02) * 0.1 + 0.12;
                        ctx.fillStyle = `rgba(150,60,180,${alpha})`;
                        ctx.beginPath();
                        ctx.arc(drawX + drift, baseY - 90 - yOff, sz, 0, Math.PI * 2);
                        ctx.fill();
                    }
                    break;
                case 23: // Blood Plains - distant bone spires and red mist
                    if (hash > 55) {
                        const h2 = 50 + hash % 40;
                        ctx.fillStyle = 'rgba(80,20,20,0.2)';
                        ctx.fillRect(drawX - 3, baseY - h2 - yOff, 6, h2);
                        ctx.fillRect(drawX - 8, baseY - h2 * 0.6 - yOff, 4, 3);
                        ctx.fillRect(drawX + 4, baseY - h2 * 0.7 - yOff, 4, 3);
                    }
                    break;
                case 8: // Swamp - dead tree silhouettes in fog
                    if (hash > 50) {
                        const h2 = 40 + hash % 30;
                        ctx.fillStyle = 'rgba(30,40,20,0.15)';
                        ctx.fillRect(drawX - 2, baseY - h2 - yOff, 4, h2);
                        // Drooping branches
                        ctx.fillRect(drawX - 10, baseY - h2 * 0.7 - yOff, 8, 2);
                        ctx.fillRect(drawX + 3, baseY - h2 * 0.6 - yOff, 7, 2);
                    }
                    break;
                case 15: // Dark Forest - massive shadowy tree silhouettes
                    if (hash > 40) {
                        const h2 = 70 + hash % 40;
                        ctx.fillStyle = 'rgba(5,15,5,0.2)';
                        ctx.fillRect(drawX - 4, baseY - h2 - yOff, 8, h2);
                        // Large canopy
                        ctx.fillStyle = 'rgba(5,15,5,0.12)';
                        ctx.fillRect(drawX - 20, baseY - h2 - yOff - 10, 40, 15);
                        ctx.fillRect(drawX - 15, baseY - h2 - yOff - 18, 30, 10);
                    }
                    break;
                case 28: // Tidal Flats - distant shipwrecks and lighthouse
                    if (hash > 70) {
                        const h2 = 35 + hash % 25;
                        ctx.fillStyle = 'rgba(80,100,100,0.15)';
                        // Broken mast
                        ctx.fillRect(drawX - 2, baseY - h2 - yOff, 4, h2);
                        ctx.fillRect(drawX - 10, baseY - h2 * 0.8 - yOff, 20, 2);
                        // Hull
                        ctx.fillRect(drawX - 15, baseY - yOff - 5, 30, 5);
                    }
                    break;
                case 26: // Petrified Wastes - ancient stone pillars
                    if (hash > 50) {
                        const h2 = 45 + hash % 35;
                        ctx.fillStyle = 'rgba(100,90,70,0.15)';
                        ctx.fillRect(drawX - 5, baseY - h2 - yOff, 10, h2);
                        // Broken top
                        ctx.fillRect(drawX - 8, baseY - h2 - yOff, 3, 5);
                        ctx.fillRect(drawX + 5, baseY - h2 - yOff + 3, 3, 4);
                    }
                    break;
                case 30: // Living Caves - pulsating organic masses
                    if (hash > 65) {
                        const pulse = Math.sin(time / 700 + fx * 0.015) * 8;
                        const sz = 25 + pulse;
                        const alpha = 0.15 + pulse * 0.01;
                        ctx.fillStyle = `rgba(140,50,50,${alpha})`;
                        ctx.beginPath();
                        ctx.arc(drawX, baseY - 70 - yOff, sz, 0, Math.PI * 2);
                        ctx.fill();
                        ctx.strokeStyle = `rgba(180,60,60,${alpha * 0.6})`;
                        ctx.lineWidth = 2;
                        ctx.beginPath();
                        ctx.moveTo(drawX, baseY - 70 - yOff);
                        ctx.lineTo(drawX - sz, baseY - 70 - yOff - sz/2);
                        ctx.moveTo(drawX, baseY - 70 - yOff);
                        ctx.lineTo(drawX + sz, baseY - 70 - yOff - sz/2);
                        ctx.stroke();
                    }
                    break;
                case 2: case 16: // Plains/Flower Meadow - distant rolling hills
                    if (hash > 45) {
                        const hh = 30 + hash % 25;
                        ctx.fillStyle = 'rgba(120,170,80,0.08)';
                        ctx.beginPath();
                        ctx.moveTo(drawX - 60, baseY - yOff);
                        ctx.quadraticCurveTo(drawX, baseY - hh - yOff, drawX + 60, baseY - yOff);
                        ctx.fill();
                    }
                    break;
                case 3: // Forest - distant tree silhouettes
                    if (hash > 35) {
                        const th = 40 + hash % 30;
                        ctx.fillStyle = 'rgba(30,80,25,0.1)';
                        ctx.beginPath();
                        ctx.moveTo(drawX - 15, baseY - yOff);
                        ctx.lineTo(drawX - 20, baseY - th * 0.6 - yOff);
                        ctx.lineTo(drawX - 5, baseY - th - yOff);
                        ctx.lineTo(drawX + 5, baseY - th - yOff);
                        ctx.lineTo(drawX + 20, baseY - th * 0.6 - yOff);
                        ctx.lineTo(drawX + 15, baseY - yOff);
                        ctx.fill();
                    }
                    break;
                case 1: // Taiga - distant pine silhouettes + aurora at night
                    if (hash > 40) {
                        const ph = 35 + hash % 30;
                        ctx.fillStyle = 'rgba(20,50,30,0.12)';
                        ctx.beginPath();
                        ctx.moveTo(drawX - 5, baseY - yOff);
                        ctx.lineTo(drawX, baseY - ph - yOff);
                        ctx.lineTo(drawX + 5, baseY - yOff);
                        ctx.fill();
                        ctx.moveTo(drawX + 15, baseY - yOff);
                        ctx.lineTo(drawX + 18, baseY - ph * 0.7 - yOff);
                        ctx.lineTo(drawX + 21, baseY - yOff);
                        ctx.fill();
                    }
                    break;
                case 5: case 6: case 17: // Desert/Red Desert/Badlands - distant dunes/mesas
                    if (hash > 50) {
                        const dh = 25 + hash % 20;
                        const dCol = biome === 17 ? 'rgba(180,100,50,0.1)' : 'rgba(210,190,130,0.08)';
                        ctx.fillStyle = dCol;
                        ctx.beginPath();
                        ctx.moveTo(drawX - 50, baseY - yOff);
                        ctx.quadraticCurveTo(drawX - 20, baseY - dh - yOff, drawX, baseY - dh * 0.8 - yOff);
                        ctx.quadraticCurveTo(drawX + 20, baseY - dh - yOff, drawX + 50, baseY - yOff);
                        ctx.fill();
                        // Mirage shimmer
                        if (biome !== 17) {
                            const mirage = Math.sin(time / 400 + fx * 0.02) * 0.06;
                            if (mirage > 0.02) {
                                ctx.fillStyle = `rgba(255,240,200,${mirage})`;
                                ctx.fillRect(drawX - 30, baseY - 5 - yOff, 60, 3);
                            }
                        }
                    }
                    break;
                case 7: // Jungle - multi-layer canopy + distant birds
                    if (hash > 30) {
                        const jh = 50 + hash % 30;
                        ctx.fillStyle = 'rgba(20,80,20,0.1)';
                        ctx.beginPath();
                        ctx.moveTo(drawX - 30, baseY - yOff);
                        ctx.quadraticCurveTo(drawX - 15, baseY - jh - yOff, drawX, baseY - jh * 0.9 - yOff);
                        ctx.quadraticCurveTo(drawX + 15, baseY - jh - yOff, drawX + 30, baseY - yOff);
                        ctx.fill();
                    }
                    break;
                case 8: // Swamp - gnarled tree silhouettes + fog
                    if (hash > 55) {
                        const sh = 30 + hash % 20;
                        ctx.strokeStyle = 'rgba(40,50,30,0.15)';
                        ctx.lineWidth = 3;
                        ctx.beginPath();
                        ctx.moveTo(drawX, baseY - yOff);
                        ctx.quadraticCurveTo(drawX + 5, baseY - sh * 0.5 - yOff, drawX - 3, baseY - sh - yOff);
                        ctx.stroke();
                        // Branches
                        ctx.lineWidth = 1;
                        ctx.beginPath();
                        ctx.moveTo(drawX - 3, baseY - sh - yOff);
                        ctx.lineTo(drawX - 12, baseY - sh + 5 - yOff);
                        ctx.moveTo(drawX - 3, baseY - sh - yOff);
                        ctx.lineTo(drawX + 8, baseY - sh + 3 - yOff);
                        ctx.stroke();
                    }
                    break;
                case 9: // Beach - distant ships on horizon
                    if (hash > 80) {
                        const shipY = baseY - 60 - yOff;
                        const bob3 = Math.sin(time / 1200 + fx * 0.01) * 3;
                        ctx.fillStyle = 'rgba(80,60,40,0.12)';
                        ctx.fillRect(drawX - 8, shipY + bob3, 16, 8);
                        // Sail
                        ctx.fillStyle = 'rgba(220,210,190,0.15)';
                        ctx.beginPath();
                        ctx.moveTo(drawX, shipY + bob3);
                        ctx.lineTo(drawX, shipY - 15 + bob3);
                        ctx.lineTo(drawX + 8, shipY + bob3);
                        ctx.fill();
                    }
                    break;
                case 14: // Savanna - acacia silhouettes on horizon
                    if (hash > 50) {
                        const ah = 25 + hash % 15;
                        ctx.fillStyle = 'rgba(80,60,20,0.1)';
                        // Trunk
                        ctx.fillRect(drawX - 1, baseY - ah - yOff, 2, ah);
                        // Flat canopy
                        ctx.beginPath();
                        ctx.moveTo(drawX - 20, baseY - ah - yOff);
                        ctx.quadraticCurveTo(drawX, baseY - ah - 8 - yOff, drawX + 20, baseY - ah - yOff);
                        ctx.fill();
                    }
                    break;
                case 15: // Dark Forest - massive tree silhouettes + glowing eyes
                    if (hash > 40) {
                        const dfh = 60 + hash % 30;
                        ctx.fillStyle = 'rgba(10,20,8,0.15)';
                        ctx.beginPath();
                        ctx.moveTo(drawX - 20, baseY - yOff);
                        ctx.lineTo(drawX - 25, baseY - dfh * 0.5 - yOff);
                        ctx.lineTo(drawX - 10, baseY - dfh - yOff);
                        ctx.lineTo(drawX + 10, baseY - dfh - yOff);
                        ctx.lineTo(drawX + 25, baseY - dfh * 0.5 - yOff);
                        ctx.lineTo(drawX + 20, baseY - yOff);
                        ctx.fill();
                        // Glowing eyes in darkness
                        const eyeGlow = Math.sin(time / 800 + fx * 0.03) * 0.5 + 0.5;
                        if (eyeGlow > 0.5) {
                            ctx.fillStyle = `rgba(180,255,60,${(eyeGlow - 0.5) * 0.3})`;
                            ctx.fillRect(drawX - 5, baseY - dfh * 0.4 - yOff, 2, 2);
                            ctx.fillRect(drawX + 3, baseY - dfh * 0.4 - yOff, 2, 2);
                        }
                    }
                    break;
                case 4: // Cherry Grove - distant cherry trees with petals
                    if (hash > 50) {
                        const ch = 30 + hash % 20;
                        ctx.fillStyle = 'rgba(120,50,50,0.08)';
                        ctx.fillRect(drawX - 1, baseY - ch - yOff, 2, ch);
                        ctx.fillStyle = 'rgba(255,150,180,0.1)';
                        ctx.beginPath();
                        ctx.arc(drawX, baseY - ch - 5 - yOff, 12, 0, Math.PI * 2);
                        ctx.fill();
                    }
                    break;
                case 12: // Mushroom Fields - giant mushroom silhouettes
                    if (hash > 45) {
                        const msh = 40 + hash % 25;
                        ctx.fillStyle = 'rgba(100,40,120,0.12)';
                        ctx.fillRect(drawX - 3, baseY - msh - yOff, 6, msh);
                        ctx.beginPath();
                        ctx.arc(drawX, baseY - msh - yOff, 18, Math.PI, 0);
                        ctx.fill();
                        const sporePulse2 = Math.sin(time / 600 + fx * 0.02) * 0.05 + 0.08;
                        ctx.fillStyle = `rgba(150,80,200,${sporePulse2})`;
                        ctx.beginPath();
                        ctx.arc(drawX, baseY - msh - 10 - yOff, 25, 0, Math.PI * 2);
                        ctx.fill();
                    }
                    break;
                case 23: // Blood Plains - distant blood pillars/altars
                    if (hash > 60) {
                        const bph = 40 + hash % 25;
                        const bpGlow = Math.sin(time / 500 + fx * 0.015) * 0.08 + 0.12;
                        ctx.fillStyle = `rgba(100,20,20,${bpGlow})`;
                        ctx.fillRect(drawX - 4, baseY - bph - yOff, 8, bph);
                        ctx.fillStyle = `rgba(180,30,30,${bpGlow * 0.5})`;
                        ctx.fillRect(drawX - 2, baseY - bph - 3 - yOff, 4, 3);
                    }
                    break;
                case 22: // Overgrown Ruins - broken columns with vines
                    if (hash > 55) {
                        const rh = 30 + hash % 20;
                        ctx.fillStyle = 'rgba(80,100,60,0.1)';
                        ctx.fillRect(drawX - 5, baseY - rh - yOff, 10, rh);
                        // Broken top
                        ctx.fillRect(drawX - 7, baseY - rh - yOff, 14, 3);
                        // Vine
                        ctx.strokeStyle = 'rgba(50,100,30,0.12)';
                        ctx.lineWidth = 1;
                        ctx.beginPath();
                        ctx.moveTo(drawX + 5, baseY - rh - yOff);
                        ctx.quadraticCurveTo(drawX + 8, baseY - rh * 0.5 - yOff, drawX + 3, baseY - yOff);
                        ctx.stroke();
                    }
                    break;
                case 10: // Corruption - twisted dead trees
                    if (hash > 50) {
                        const cth = 35 + hash % 20;
                        ctx.strokeStyle = 'rgba(60,20,80,0.15)';
                        ctx.lineWidth = 3;
                        ctx.beginPath();
                        ctx.moveTo(drawX, baseY - yOff);
                        ctx.quadraticCurveTo(drawX + 8, baseY - cth * 0.6 - yOff, drawX - 5, baseY - cth - yOff);
                        ctx.stroke();
                        ctx.lineWidth = 1;
                        ctx.beginPath();
                        ctx.moveTo(drawX - 5, baseY - cth - yOff);
                        ctx.lineTo(drawX - 15, baseY - cth + 5 - yOff);
                        ctx.moveTo(drawX - 5, baseY - cth - yOff);
                        ctx.lineTo(drawX + 5, baseY - cth - 5 - yOff);
                        ctx.stroke();
                    }
                    break;
                case 11: // Hallow - rainbow crystal towers
                    if (hash > 55) {
                        const hth = 40 + hash % 20;
                        const hOff = time / 600 + fx * 0.01;
                        const hr = 180 + Math.sin(hOff) * 40 | 0;
                        const hg = 180 + Math.sin(hOff + 2) * 40 | 0;
                        const hb = 220 + Math.sin(hOff + 4) * 35 | 0;
                        ctx.fillStyle = `rgba(${hr},${hg},${hb},0.1)`;
                        ctx.beginPath();
                        ctx.moveTo(drawX - 6, baseY - yOff);
                        ctx.lineTo(drawX, baseY - hth - yOff);
                        ctx.lineTo(drawX + 6, baseY - yOff);
                        ctx.fill();
                    }
                    break;
            }
        }
    }
    drawTrees(ctx, L, camX, screenL, screenR, baseY, yOff, biome) {
        const sp = 35, visL = camX * L.spd - 400, visR = camX * L.spd + 400;
        const first = Math.floor(visL / sp) * sp;
        for (let tlx = first; tlx <= visR; tlx += sp) {
            const hash = ((tlx * 31337 + 7919) >>> 0); if ((hash % 100) > 50) continue;
            const drawX = tlx + camX * (1 - L.spd); if (drawX < screenL - 30 || drawX > screenR + 30) continue;
            const nlx = tlx * L.sc; const terrH = L.n.oct(nlx, 10, 3, 0.5, 2) * L.amp + L.base;
            const tBase = baseY - terrH - yOff; const tH = 12 + (hash % 15);
            ctx.fillStyle = L.col; ctx.fillRect(drawX - 1, tBase - tH * 0.3, 3, tH * 0.4);
            if (biome === 14) { ctx.fillRect(drawX - 7, tBase - tH * 0.3, 15, 3); }
            else if ([0, 1, 18].includes(biome)) { ctx.beginPath(); ctx.moveTo(drawX - 5, tBase); ctx.lineTo(drawX + 1, tBase - tH * 0.35); ctx.lineTo(drawX + 7, tBase); ctx.fill(); }
            else { ctx.beginPath(); ctx.arc(drawX + 1, tBase - tH * 0.15, 6, 0, Math.PI * 2); ctx.fill(); }
        }
    }
    _allColors() {
        if (!this._colorCache) {
            // Enhanced colors with more atmospheric depth per biome
            this._colorCache = [
                ['#2a3a4a', '#1a2a3a', '#0a1a2a'], // 0 Tundra - cold blues
                ['#2a3a35', '#1a2a2a', '#0a1a1a'], // 1 Taiga - dark greens
                ['#3a5a2a', '#2a4a1a', '#1a3a0a'], // 2 Plains - fresh greens
                ['#2a4a1a', '#1a3a0a', '#0a2a00'], // 3 Forest - deep forest
                ['#5a3040', '#4a2030', '#3a1020'], // 4 Cherry Grove - pink tints
                ['#6a5a30', '#5a4a20', '#4a3a10'], // 5 Desert - sandy yellows
                ['#5a3a20', '#4a2a15', '#3a1a0a'], // 6 Red Desert - burnt reds
                ['#1a4a1a', '#0a3a0a', '#003000'], // 7 Jungle - lush deep green
                ['#3a4a2a', '#2a3a1a', '#1a2a0a'], // 8 Swamp - murky greens
                ['#3a5a6a', '#2a4a5a', '#1a3a4a'], // 9 Beach - coastal blues
                ['#3a1a4a', '#2a0a3a', '#1a002a'], // 10 Corruption - deep purples
                ['#4a4a7a', '#3a3a6a', '#2a2a5a'], // 11 Hallow - holy purples
                ['#3a2a4a', '#2a1a3a', '#1a0a2a'], // 12 Mushroom - purple shrooms
                ['#4a1a0a', '#3a0a00', '#2a0000'], // 13 Volcanic - fiery reds
                ['#5a4a1a', '#4a3a0a', '#3a2a00'], // 14 Savanna - dry yellows
                ['#1a2a0a', '#0a1a00', '#000a00'], // 15 Dark Forest - pitch black
                ['#3a5a2a', '#2a4a1a', '#1a3a0a'], // 16 Flower Meadow - vibrant
                ['#5a3a20', '#4a2a15', '#3a1a0a'], // 17 Badlands - clay reds
                ['#2a3a4a', '#1a2a3a', '#0a1a2a'], // 18 Frozen Shore - icy
                ['#3a4a7a', '#2a3a6a', '#1a2a5a'], // 19 Crystal Caverns - crystalline blues
                ['#0a0a2a', '#050518', '#020210'], // 20 Abyss - pitch black with hints
                ['#5a2a10', '#4a1a08', '#3a0a00'], // 21 Ember Wastes - smoldering
                ['#2a4a2a', '#1a3a1a', '#0a2a0a'], // 22 Overgrown Ruins - mossy
                ['#5a1a1a', '#4a0a0a', '#3a0000'], // 23 Blood Plains - blood red
                ['#3a5a6a', '#2a4a5a', '#1a3a4a'], // 24 Shimmer Glades - ethereal cyan
                ['#3a1a5a', '#2a0a4a', '#1a0038'], // 25 Fungal Abyss - toxic purple
                ['#5a5040', '#4a4030', '#3a3020'], // 26 Petrified Wastes - ancient stone
                ['#4a6a7a', '#3a5a6a', '#2a4a5a'], // 27 Prism Shores - rainbow shimmer
                ['#4a6a6a', '#3a5a5a', '#2a4a4a'], // 28 Tidal Flats - sea greens
                ['#3a3a5a', '#2a2a4a', '#1a1a3a'], // 29 Storm Peaks - stormy purples
                ['#5a2a2a', '#4a1a1a', '#3a0a0a']  // 30 Living Caves - fleshy reds
            ];
        }
        return this._colorCache;
    }
    getColors(b) {
        const c = this._allColors();
        return c[b] || c[2];
    }
    getBlendedColors(weights) {
        const all = this._allColors();
        const result = ['', '', ''];
        for (let li = 0; li < 3; li++) {
            let r = 0, g = 0, b = 0;
            for (const bi in weights) {
                const c = (all[bi] || all[2])[li];
                const w = weights[bi];
                r += parseInt(c.slice(1, 3), 16) * w;
                g += parseInt(c.slice(3, 5), 16) * w;
                b += parseInt(c.slice(5, 7), 16) * w;
            }
            result[li] = `rgb(${r | 0},${g | 0},${b | 0})`;
        }
        return result;
    }
}

// === AMBIENT PARTICLES — Enhanced per biome ===
export class AmbientSystem {
    constructor() { this.particles = []; this.maxP = 120; this._weatherTimer = 0; this._weatherIntensity = 0; this._rainParticles = []; }
    update(dt, camX, camY, vw, vh, biome, underground, isNight, weather) {
        for (let i = this.particles.length - 1; i >= 0; i--) {
            const p = this.particles[i]; p.life -= dt; p.x += p.vx * dt / 16; p.y += p.vy * dt / 16;
            if (p.type === 'butterfly') { p.x += Math.sin(p.life / 300 + p.phase) * 0.8; p.y += Math.sin(p.life / 200 + p.phase) * 0.4; }
            if (p.type === 'firefly') { p.x += Math.sin(p.life / 400 + p.phase) * 0.5; p.y += Math.cos(p.life / 350 + p.phase) * 0.5; }
            if (p.type === 'snow') { p.x += Math.sin(p.life / 500 + p.phase) * 0.3; }
            if (p.type === 'sandstorm') { p.x += dt * 0.08; }
            if (p.type === 'ash') { p.y -= Math.abs(Math.sin(p.life / 300 + p.phase)) * 0.2; }
            if (p.type === 'spore') { p.x += Math.sin(p.life / 600 + p.phase) * 0.4; p.y += Math.cos(p.life / 500 + p.phase) * 0.3; }
            if (p.rot !== undefined) p.rot += p.rotSpd * dt / 16;
            if (p.life <= 0) this.particles.splice(i, 1);
        }
        // World-space rain particles (splashes on ground)
        if (weather && weather.intensity > 0.1 && !underground) {
            // Update existing rain splashes
            for (let i = this._rainParticles.length - 1; i >= 0; i--) {
                const rp = this._rainParticles[i];
                rp.life -= dt;
                rp.sz *= 1.03; // expand
                if (rp.life <= 0) this._rainParticles.splice(i, 1);
            }
            // Spawn new splashes
            if (this._rainParticles.length < 30 && Math.random() < weather.intensity * 0.4) {
                this._rainParticles.push({
                    x: camX - vw / 2 + Math.random() * vw,
                    y: camY + vh * 0.3 + Math.random() * vh * 0.2,
                    sz: 1.5, life: 200, maxLife: 200
                });
            }
        }
        if (this.particles.length >= this.maxP || Math.random() > 0.5) return;
        const sx = camX - vw / 2 + Math.random() * vw, sy = camY - vh / 2 + Math.random() * vh;
        const push = (vx, vy, c, life, sz, type) => {
            this.particles.push({
                x: sx, y: sy, vx, vy, c, life, maxLife: life, sz: sz || 2, type: type || 'rect',
                rot: type === 'leaf' ? Math.random() * Math.PI * 2 : undefined, rotSpd: type === 'leaf' ? (Math.random() - 0.5) * 0.02 : 0,
                phase: Math.random() * Math.PI * 2
            });
        };
        if (underground) {
            if (Math.random() > 0.85) push(0, 0.3, 'rgba(150,150,255,0.3)', 3000, 1);
            // Underground biome-specific ambient
            if (Math.random() > 0.9) {
                // Dripping water particles in caves
                push(0, 0.8, 'rgba(100,160,220,0.5)', 1500, 1);
            }
            return;
        }
        switch (biome) {
            case 0: case 18: // Tundra/Frozen Shore — heavy snowfall + wind gusts
                // Dense snowflakes (multiple per frame for heavy snow effect)
                push((Math.random() - 0.5) * 0.8 + 0.3, 0.6 + Math.random() * 0.3, 'rgba(240,245,255,0.85)', 4000, 2 + Math.random(), 'snow');
                if (Math.random() > 0.4) push((Math.random() - 0.5) * 0.5 + 0.4, 0.4, 'rgba(220,230,245,0.6)', 3500, 1.5 + Math.random(), 'snow');
                // Wind-driven snow streaks
                if (Math.random() > 0.7) push(1.5 + Math.random(), 0.1, 'rgba(200,220,240,0.3)', 1500, 1); break;
            case 1: // Taiga — light snow + falling pine needles
                if (Math.random() > 0.4) push((Math.random() - 0.5) * 0.3 + 0.2, 0.35, 'rgba(230,240,250,0.6)', 3000, 1.5 + Math.random() * 0.5, 'snow');
                else push((Math.random() - 0.5) * 0.5, 0.25, 'rgba(80,130,60,0.5)', 2200, 2, 'leaf'); break;
            case 2: if (isNight && Math.random() > 0.6) push((Math.random() - 0.5) * 0.2, Math.random() * 0.2 - 0.1, 'rgba(200,255,150,0.6)', 3000, 2, 'firefly');
            else if (Math.random() > 0.7) push((Math.random() - 0.5) * 0.3, -0.15, 'rgba(255,255,240,0.3)', 2000, 1); break;
            case 3: push((Math.random() - 0.5) * 0.3, 0.2, 'rgba(80,140,50,0.4)', 2000, 2, 'leaf'); break;
            case 4: push((Math.random() - 0.5) * 0.6, 0.25, 'rgba(255,170,200,0.7)', 2800, 2, 'leaf'); break;
            case 5: case 6: // Desert — sandstorm particles + heat haze
                push(2 + Math.random() * 1.5, (Math.random() - 0.5) * 0.3, 'rgba(210,190,140,0.45)', 1000, 1.5, 'sandstorm');
                if (Math.random() > 0.5) push(1.8 + Math.random(), 0.05, 'rgba(190,170,120,0.3)', 800, 1, 'sandstorm');
                if (Math.random() > 0.85) push(1 + Math.random(), 0.1, 'rgba(200,180,130,0.2)', 1500, 1); break;
            case 7: if (isNight) push((Math.random() - 0.5) * 0.3, Math.random() * 0.2 - 0.1, 'rgba(150,255,80,0.7)', 3500, 2, 'firefly');
            else push((Math.random() - 0.5) * 0.1, 0.4, 'rgba(150,200,255,0.3)', 1500, 1); break;
            case 8: if (Math.random() > 0.5) push((Math.random() - 0.5) * 0.15, Math.random() * 0.2 - 0.1, 'rgba(120,200,80,0.6)', 3500, 2, 'firefly');
            else push((Math.random() - 0.5) * 0.3, -0.3, 'rgba(180,220,100,0.4)', 2000, 2); break;
            case 9: push((Math.random() - 0.5) * 0.5, 0.1, 'rgba(200,230,255,0.3)', 1500, 1); break;
            case 10: push((Math.random() - 0.5) * 0.6, -0.5, 'rgba(130,30,200,0.7)', 2500, 2);
                if (Math.random() > 0.7) push((Math.random() - 0.5) * 0.3, -0.8, 'rgba(80,10,150,0.5)', 1800, 3); break;
            case 11: {
                const rc = ['rgba(255,100,100,0.6)', 'rgba(100,100,255,0.6)', 'rgba(255,255,100,0.6)', 'rgba(100,255,100,0.6)', 'rgba(255,100,255,0.6)'];
                push((Math.random() - 0.5) * 0.4, 0.05, rc[Math.floor(Math.random() * rc.length)], 2500, 2); break;
            }
            case 12: push((Math.random() - 0.5) * 0.3, -0.25, 'rgba(100,255,150,0.5)', 3000, 2);
                if (Math.random() > 0.6) push((Math.random() - 0.5) * 0.2, -0.15, 'rgba(80,200,120,0.3)', 2500, 1); break;
            case 13: // Volcanic — rising ash + floating embers
                push((Math.random() - 0.5) * 0.5, -0.8, 'rgba(255,120,30,0.7)', 1200, 2, 'ash');
                if (Math.random() > 0.4) push((Math.random() - 0.5) * 0.6, -0.5, 'rgba(100,90,80,0.4)', 2500, 2, 'ash');
                if (Math.random() > 0.6) push((Math.random() - 0.5) * 0.3, 0.5, 'rgba(60,60,60,0.3)', 3000, 3); break;
            case 14: push(0.6 + Math.random() * 0.4, 0.05, 'rgba(200,180,100,0.35)', 1500, 1); break;
            case 15: if (isNight || Math.random() > 0.5) push((Math.random() - 0.5) * 0.15, Math.random() * 0.15 - 0.08, 'rgba(160,255,80,0.75)', 4000, 2, 'firefly'); break;
            case 16: {
                const cs = ['rgba(255,180,180,0.5)', 'rgba(180,180,255,0.5)', 'rgba(255,255,180,0.5)', 'rgba(180,255,180,0.5)'];
                if (Math.random() > 0.4) push((Math.random() - 0.5) * 0.5, -0.1, cs[Math.floor(Math.random() * 4)], 2800, 2, 'leaf');
                if (Math.random() > 0.7) push((Math.random() - 0.5) * 0.3, Math.random() * 0.2 - 0.1, 'rgba(255,220,50,0.6)', 3000, 2, 'butterfly'); break;
            }
            case 17: // Badlands — dust storm + tumbling sand grains
                push(1.5 + Math.random() * 0.8, (Math.random() - 0.5) * 0.2, 'rgba(180,120,60,0.4)', 1000, 1.5, 'sandstorm');
                if (Math.random() > 0.6) push(1 + Math.random(), 0.08, 'rgba(160,100,50,0.3)', 1200, 1); break;
            case 19: push((Math.random() - 0.5) * 0.3, -0.2, 'rgba(140,170,255,0.7)', 2800, 2);
                if (Math.random() > 0.5) push((Math.random() - 0.5) * 0.2, Math.random() * 0.15 - 0.08, 'rgba(170,200,255,0.5)', 3000, 2, 'firefly'); break;
            case 20: if (Math.random() > 0.7) push((Math.random() - 0.5) * 0.1, Math.random() * 0.1 - 0.05, 'rgba(30,200,150,0.5)', 4000, 2, 'firefly'); break;
            case 21: // Ember Wastes — dense rising ash + sparks
                push((Math.random() - 0.5) * 0.6, -1.2, 'rgba(255,140,40,0.6)', 900, 2, 'ash');
                if (Math.random() > 0.3) push((Math.random() - 0.5) * 0.4, -0.6, 'rgba(120,80,40,0.35)', 2000, 2, 'ash');
                if (Math.random() > 0.5) push((Math.random() - 0.5) * 0.5, 0.5, 'rgba(80,50,25,0.3)', 2500, 3); break;
            case 22: push((Math.random() - 0.5) * 0.3, -0.1, 'rgba(100,200,80,0.5)', 2500, 2, 'leaf');
                if (Math.random() > 0.6) push((Math.random() - 0.5) * 0.2, Math.random() * 0.2 - 0.1, 'rgba(80,180,60,0.6)', 3000, 2, 'firefly'); break;
            case 23: push((Math.random() - 0.5) * 0.6, -0.8, 'rgba(200,30,30,0.6)', 2000, 2);
                if (Math.random() > 0.5) push((Math.random() - 0.5) * 0.3, 0.2, 'rgba(150,20,20,0.4)', 1500, 2); break;
            case 24: {
                const sc2 = ['rgba(150,220,255,0.5)', 'rgba(200,180,255,0.5)', 'rgba(180,255,220,0.5)'];
                push((Math.random() - 0.5) * 0.3, -0.15, sc2[Math.floor(Math.random() * 3)], 3500, 2, 'firefly');
                if (Math.random() > 0.6) push((Math.random() - 0.5) * 0.4, Math.random() * 0.2 - 0.1, 'rgba(200,240,255,0.4)', 2500, 2, 'butterfly'); break;
            }
            case 25: // Fungal Abyss — drifting spores + bioluminescent motes
                push((Math.random() - 0.5) * 0.3, -0.15, 'rgba(170,60,200,0.5)', 4000, 2.5, 'spore');
                if (Math.random() > 0.3) push((Math.random() - 0.5) * 0.2, Math.random() * 0.15 - 0.08, 'rgba(100,255,180,0.5)', 4500, 2, 'firefly');
                if (Math.random() > 0.6) push((Math.random() - 0.5) * 0.4, -0.2, 'rgba(200,80,220,0.35)', 3000, 3, 'spore'); break;
            case 26: // Petrified Wastes — dust and time particles
                push(0.5 + Math.random() * 0.5, 0.05, 'rgba(160,140,100,0.4)', 1500, 1);
                if (Math.random() > 0.6) push((Math.random() - 0.5) * 0.3, -0.1, 'rgba(220,180,80,0.5)', 2500, 2, 'firefly'); break;
            case 27: { // Prism Shores — rainbow sparkles
                const pc = ['rgba(255,120,120,0.5)', 'rgba(120,255,120,0.5)', 'rgba(120,120,255,0.5)', 'rgba(255,255,120,0.5)', 'rgba(255,120,255,0.5)'];
                push((Math.random() - 0.5) * 0.4, -0.1, pc[Math.floor(Math.random() * pc.length)], 3000, 2, 'firefly');
                if (Math.random() > 0.5) push((Math.random() - 0.5) * 0.3, Math.random() * 0.15 - 0.08, 'rgba(255,255,255,0.4)', 2000, 2, 'butterfly'); break;
            }
            case 28: // Tidal Flats — sea spray and mist
                push(0.8 + Math.random() * 0.6, 0.1, 'rgba(180,220,240,0.4)', 1200, 1);
                if (Math.random() > 0.5) push((Math.random() - 0.5) * 0.5, -0.2, 'rgba(200,230,255,0.3)', 2000, 2); break;
            case 29: // Storm Peaks — driving rain + electric sparks + wind debris
                push(0.8 + Math.random() * 0.5, 3 + Math.random(), 'rgba(160,190,220,0.4)', 600, 1);
                if (Math.random() > 0.3) push(0.6 + Math.random() * 0.4, 2.5, 'rgba(140,170,200,0.3)', 500, 1);
                if (Math.random() > 0.75) push((Math.random() - 0.5) * 0.3, -0.5, 'rgba(200,240,255,0.9)', 400, 2, 'firefly');
                if (Math.random() > 0.9) push((Math.random() - 0.5) * 2, -1, 'rgba(255,255,255,0.7)', 200, 1); break;
            case 30: // Living Caves — blood mist and organic spores
                push((Math.random() - 0.5) * 0.3, -0.4, 'rgba(200,60,60,0.5)', 2500, 2);
                if (Math.random() > 0.4) push((Math.random() - 0.5) * 0.2, Math.random() * 0.2 - 0.1, 'rgba(255,100,80,0.4)', 3000, 2, 'firefly');
                if (Math.random() > 0.7) push((Math.random() - 0.5) * 0.5, 0.3, 'rgba(150,40,40,0.3)', 2000, 3); break;
        }
    }
    render(ctx) {
        for (const p of this.particles) {
            const alpha = Math.min(1, p.life / p.maxLife * 2.5) * 0.8; ctx.globalAlpha = alpha; ctx.fillStyle = p.c;
            if (p.type === 'leaf' && p.rot !== undefined) { ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot); ctx.fillRect(-p.sz, -p.sz / 2, p.sz * 2, p.sz); ctx.restore(); }
            else if (p.type === 'butterfly') {
                ctx.save(); ctx.translate(p.x, p.y); const wf = Math.sin(p.life / 80) * 0.5;
                ctx.fillRect(-3, 0, 3, 2); ctx.fillRect(1, 0, 3, 2); ctx.fillRect(-2 - wf, -1, 2, 1); ctx.fillRect(1 + wf, -1, 2, 1); ctx.restore();
            }
            else if (p.type === 'firefly') {
                const glow = Math.sin(p.life / 200 + p.phase) * 0.3 + 0.7;
                ctx.globalAlpha = alpha * glow; ctx.fillRect(p.x - p.sz / 2, p.y - p.sz / 2, p.sz, p.sz);
                ctx.globalAlpha = alpha * glow * 0.3; ctx.fillRect(p.x - p.sz, p.y - p.sz, p.sz * 2, p.sz * 2);
            }
            else if (p.type === 'snow') {
                // Snowflakes — slightly larger, soft square
                ctx.fillRect(p.x - p.sz / 2, p.y - p.sz / 2, p.sz, p.sz);
                // Subtle glow around snowflake
                ctx.globalAlpha = alpha * 0.15;
                ctx.fillRect(p.x - p.sz, p.y - p.sz, p.sz * 2, p.sz * 2);
            }
            else if (p.type === 'sandstorm') {
                // Elongated horizontal streaks for wind-blown sand
                ctx.fillRect(p.x - p.sz * 2, p.y - p.sz * 0.3, p.sz * 4, p.sz * 0.6);
            }
            else if (p.type === 'ash') {
                // Ash — small flickering particles
                const flicker = Math.sin(p.life / 150 + p.phase) * 0.3 + 0.7;
                ctx.globalAlpha = alpha * flicker;
                ctx.fillRect(p.x - p.sz / 2, p.y - p.sz / 2, p.sz, p.sz);
            }
            else if (p.type === 'spore') {
                // Spores — pulsing organic particles
                const pulse = Math.sin(p.life / 400 + p.phase) * 0.3 + 0.7;
                const sz = p.sz * pulse;
                ctx.globalAlpha = alpha * pulse;
                ctx.fillRect(p.x - sz / 2, p.y - sz / 2, sz, sz);
                ctx.globalAlpha = alpha * pulse * 0.2;
                ctx.fillRect(p.x - sz, p.y - sz, sz * 2, sz * 2);
            }
            else ctx.fillRect(p.x - p.sz / 2, p.y - p.sz / 2, p.sz, p.sz);
        }
        // Rain splashes (world-space)
        for (const rp of this._rainParticles) {
            const a = Math.min(1, rp.life / rp.maxLife * 2) * 0.3;
            ctx.globalAlpha = a;
            ctx.strokeStyle = 'rgba(180,210,240,0.5)';
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.arc(rp.x, rp.y, rp.sz, 0, Math.PI, true); // half-circle splash
            ctx.stroke();
        }
        ctx.globalAlpha = 1;
    }
}